<template>
  <div>
    <b-row class="pt-1 pb-0 planning-page-header-row align-items-start">
      <b-col cols="12" md="auto" class="flex-grow-1 planning-header-left-col pr-md-2">
        <BreadcrumbMenu />
        <div class="d-flex align-items-center mt-50 mb-0">
          <b-button variant="outline-secondary" size="sm" class="rounded-circle p-25" @click="goBack">
            <feather-icon icon="ArrowLeftIcon" size="16" />
          </b-button>
          <span class="ml-1 text-capitalize text-dark cursor-pointer goback-text" @click="goBack">{{ $t('actions.go_back') }}</span>
        </div>
      </b-col>
      <b-col
        cols="12"
        md="auto"
        class="d-flex align-items-center justify-content-md-end flex-wrap planning-page-header-actions mt-1 mt-md-0"
      >
        <b-button
          v-if="!isCreateMode && planningId"
          variant="success"
          size="sm"
          class="text-capitalize"
          :disabled="workflowLoading || loading"
          @click="onDownloadPlanning"
        >
          <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
          {{ $t('planning.download_planning') }}
        </b-button>
        <b-badge
          v-if="!isCreateMode && currentItem"
          variant="light-primary"
          class="text-capitalize"
        >
          {{ $t('planning.status') }}:
          <PlanningStatusBadge :status="currentItem.status" />
        </b-badge>
      </b-col>
    </b-row>

    <b-row class="mb-1 mx-2">
      <b-col cols="12" class="planning-title-col">
        <h4 class="mb-0 text-capitalize">{{ planningTitle }}</h4>
      </b-col>
    </b-row>

    <div v-if="loading && !isCreateMode" class="d-flex flex-column align-items-center justify-content-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2 mb-0 text-primary">{{ $t('actions.loading') }}</p>
    </div>
    <b-card v-else-if="loadError && !isCreateMode" class="m-2">
      <p class="text-danger mb-0">{{ $t('no_record_found') }}</p>
      <b-button variant="outline-secondary" size="sm" class="mt-2" @click="goBack">{{ $t('planning.exit') }}</b-button>
    </b-card>
    <b-card v-else class="m-2 planning-form-card">
      <div class="planning-fields-row">
        <b-row>
          <b-col cols="12" md="6" class="mb-2">
            <label class="text-capitalize">{{ $t('planning.activity_type_to_schedule') }}</label>
            <v-select
              v-model="activityType"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="activityTypeOptions"
              class="w-100"
              label="label"
              :reduce="(item) => (item != null && item.value != null ? item.value : item)"
              clearable
              :disabled="(!isCreateMode && !!planningId) || isPlanningHeaderOrTableShellDisabled || (isCreateMode && !canCreatePlanning)"
            />
          </b-col>
          <b-col cols="12" md="6" class="mb-2">
            <label class="text-capitalize">{{ $t('planning.business_division') }}</label>
            <v-select
              v-model="sectorIds"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="localSectorOptions"
              class="w-100"
              label="label"
              :reduce="reduceOptionToValue"
              :multiple="true"
              clearable
              :disabled="areDependentFieldsDisabled || isPlanningHeaderOrTableShellDisabled || isDivisionRegionUnitsSelectionLocked"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="12" md="6" class="mb-2">
            <label class="text-capitalize">{{ $t('planning.federal_entity') }}</label>
            <v-select
              v-model="regionIds"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="localRegionOptions"
              class="w-100"
              label="label"
              :reduce="reduceOptionToValue"
              :multiple="true"
              clearable
              :disabled="areDependentFieldsDisabled || isPlanningHeaderOrTableShellDisabled || isDivisionRegionUnitsSelectionLocked"
            />
          </b-col>
          <b-col cols="12" md="6" class="mb-2">
            <label class="text-capitalize">{{ $t('planning.units_label') }}</label>
            <UnitsSelectPaginated
              :value="selectedUnitIds"
              :initial-options="unitsInitialOptionsForSelect"
              :multiple="true"
              :main-unit-only="shouldFilterMainUnits"
              :exclude-disabled="true"
              :placeholder="$t('table.search') + '...'"
              :disabled="areDependentFieldsDisabled || isPlanningHeaderOrTableShellDisabled || isDivisionRegionUnitsSelectionLocked"
              @input="onUnitsInput"
            />
          </b-col>
        </b-row>
      </div>

      <b-card no-body class="selected-units-card">
        <b-card-header class="pb-50 text-capitalize">
          <h5>{{ selectedUnitsTitle }}</h5>
        </b-card-header>
        <b-card-body class="selected-units-card-body">
          <template v-if="isCreateMode">
            <div
              v-if="distinctPropertyTypesInPlanningTable.length"
              class="mb-2"
            >
              <label class="text-capitalize d-block mb-50">{{ $t('planning.property_types_in_selection') }}</label>
              <v-select
                :value="selectedPropertyTypeLabelsCreate"
                :options="propertyTypeFilterSelectOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                class="w-100"
                label="label"
                :reduce="reducePlanningPropertyTypeOption"
                :multiple="true"
                clearable
                :disabled="areDependentFieldsDisabled || isPlanningHeaderOrTableShellDisabled || isDivisionRegionUnitsSelectionLocked"
                @input="onCreatePropertyTypesFilterInput"
              />
            </div>
            <div class="planning-units-table-scroll">
            <b-table
              :items="tableRows"
              :fields="tableFields"
              responsive
              show-empty
              class="planning-units-table"
              :empty-text="$t('planning.no_units_selected')"
            >
              <template #head()="data">
                <span>{{ $tc(data.label, 1) }}</span>
              </template>
              <template #head(residual_risk_level)="data">
                <span :id="'risk-level-header-create'">{{ $tc(data.label, 1) }}</span>
                <b-tooltip :target="'risk-level-header-create'" triggers="hover">
                  <div>Alto: 6 meses</div>
                  <div>Medio-Alto: 1 año</div>
                  <div>Medio: 2 años</div>
                  <div>Medio-Bajo: 3 años</div>
                  <div>Bajo: 4 años</div>
                </b-tooltip>
              </template>
              <template #head(selected)="">
                <b-form-checkbox
                  :checked="allSelectedCreate"
                  :indeterminate="isIndeterminateCreate"
                  @change="toggleSelectAllCreate"
                />
              </template>
              <template #cell(selected)="data">
                <b-form-checkbox v-model="data.item.selected" />
              </template>
              <template #cell(unit_name)="data">
                {{ data.item.unit_name || '-' }}
              </template>
              <template #cell(category_name)="data">
                {{ data.item.category_name || '-' }}
              </template>
              <template #cell(last_sra_date)="data">
                {{ data.item.last_sra_date || '-' }}
              </template>
              <template #cell(residual_risk_level)="data">
                {{ data.item.residual_risk_level || '-' }}
              </template>
              <template #cell(start_date)="data">
                <b-form-input
                  v-model="data.item.start_date"
                  type="date"
                  @change="onStartDateChange(data.item)"
                  class="form-control form-control-sm"
                />
              </template>
              <template #cell(end_date)="data">
                <b-form-input
                  v-model="data.item.end_date"
                  type="date"
                  :min="data.item.start_date || null"
                  :disabled="!data.item.start_date || isEditLocked"
                  class="form-control form-control-sm"
                />
              </template>
              <template #cell(assigned_to_id)="data">
                <div class="assigned-to-cell">
                  <v-select
                    v-model="data.item.assigned_to_id"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="localUserOptions"
                    class="w-100"
                    label="name"
                    :filter-by="filterUsersByNameEmailAndUser"
                    :reduce="(u) => (u != null && u.value != null ? u.value : u && u.id)"
                    clearable
                    size="sm"
                    append-to-body
                    :calculate-position="withPopperTop"
                  />
                </div>
              </template>
              <template #cell(actions)="data">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0 text-danger"
                  @click="removeRow(data.index)"
                >
                  <feather-icon icon="TrashIcon" size="16" />
                </b-button>
              </template>
            </b-table>
            </div>
          </template>
          <template v-else>
            <div v-if="isReviewFlowMode" class="d-flex align-items-center flex-wrap mb-1">
              <b-button size="sm" variant="outline-primary" class="mr-1 mb-1" :disabled="isPlanningHeaderOrTableShellDisabled" @click="toggleAddExtraUnits">
                {{ $t('planning.add_extra_units') }}
              </b-button>
              <div v-if="showAddExtraUnits" class="review-extra-units-wrapper mr-1 mb-1">
                <UnitsSelectPaginated
                  :value="selectedExtraUnitIds"
                  :initial-options="reviewExtraUnitsInitialOptions"
                  :multiple="true"
                  :main-unit-only="shouldFilterMainUnits"
                  :exclude-disabled="true"
                  :disabled="isPlanningHeaderOrTableShellDisabled"
                  :dropup="true"
                  :append-to-body="true"
                  :placeholder="$t('planning.add_extra_units_placeholder')"
                  @input="onExtraUnitsInput"
                />
              </div>
              <b-button
                v-if="showAddExtraUnits"
                size="sm"
                variant="primary"
                class="mb-1"
                :disabled="!selectedExtraUnitIds.length || isPlanningHeaderOrTableShellDisabled"
                @click="onAddExtraUnits"
              >
                {{ $t('actions.add') }}
              </b-button>
            </div>
            <template v-if="showEditableItemsTable">
            <div
              v-if="distinctPropertyTypesInPlanningTable.length"
              class="mb-2"
            >
              <label class="text-capitalize d-block mb-50">{{ $t('planning.property_types_in_selection') }}</label>
              <v-select
                :value="selectedPropertyTypeLabelsEdit"
                :options="propertyTypeFilterSelectOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                class="w-100"
                label="label"
                :reduce="reducePlanningPropertyTypeOption"
                :multiple="true"
                clearable
                :disabled="isPlanningHeaderOrTableShellDisabled || isDraftPlanningEditBlocked || isDivisionRegionUnitsSelectionLocked"
                @input="onEditPropertyTypesFilterInput"
              />
            </div>
            <div class="planning-units-table-scroll">
            <b-table
              :items="editModeTableItems"
              :fields="editableItemsTableFieldsResolved"
              :tbody-tr-class="getPlanningEditTableRowClass"
              responsive
              show-empty
              class="planning-units-table"
              :empty-text="$t('planning.no_units_selected')"
            >
                <template #head()="data">
                  <span>{{ $tc(data.label, 1) }}</span>
                </template>
                <template #head(residual_risk_level)="data">
                  <span :id="'risk-level-header-edit'">{{ $tc(data.label, 1) }}</span>
                  <b-tooltip :target="'risk-level-header-edit'" triggers="hover">
                    <div>Alto: 6 meses</div>
                    <div>Medio-Alto: 1 año</div>
                    <div>Medio: 2 años</div>
                    <div>Medio-Bajo: 3 años</div>
                    <div>Bajo: 4 años</div>
                  </b-tooltip>
                </template>
                <template #head(selected)="">
                  <b-form-checkbox
                    :checked="allSelectedEdit"
                    :indeterminate="isIndeterminateEdit"
                    :disabled="isPlanningHeaderOrTableShellDisabled || !editRowsForBulkSelectInEditMode.length"
                    @change="toggleSelectAllEdit"
                  />
                </template>
                <template #cell(selected)="data">
                  <b-form-checkbox v-model="data.item.selected" :disabled="isPlanningRowCheckboxDisabled(data.item)" />
                </template>
                <template #cell(unit_name)="data">
                  <div>
                    <div>{{ data.item.unit_name || '-' }}</div>
                    <div
                      v-if="!data.item.is_extra && planningCommentDisplayValue(data.item).trim() !== ''"
                      class="planning-item-comment-line small text-muted mt-25"
                    >
                      {{ $t('planning.item_comment_prefix') }}: {{ planningCommentDisplayValue(data.item) }}
                    </div>
                  </div>
                </template>
                <template #cell(category_name)="data">
                  {{ data.item.category_name || '-' }}
                </template>
                <template #cell(last_sra_date)="data">
                  {{ data.item.last_sra_date || '-' }}
                </template>
                <template #cell(residual_risk_level)="data">
                  {{ data.item.residual_risk_level || '-' }}
                </template>
                <template #cell(start_date)="data">
                  <b-form-input
                    v-model="data.item.start_date"
                    type="date"
                    @change="onStartDateChange(data.item)"
                    :disabled="isPlanningRowFieldsDisabled(data.item)"
                    class="form-control form-control-sm"
                  />
                </template>
                <template #cell(end_date)="data">
                  <b-form-input
                    v-model="data.item.end_date"
                    type="date"
                    :min="data.item.start_date || null"
                    :disabled="!data.item.start_date || isPlanningRowFieldsDisabled(data.item)"
                    class="form-control form-control-sm"
                  />
                </template>
                <template #cell(user_id)="data">
                  <div class="assigned-to-cell">
                    <v-select
                      v-model="data.item.user_id"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="userOptions"
                      label="name"
                      :filter-by="filterUsersByNameEmailAndUser"
                      :reduce="(u) => (u != null && u.value != null ? u.value : u)"
                      clearable
                      :disabled="isPlanningRowFieldsDisabled(data.item)"
                      size="sm"
                      append-to-body
                      :calculate-position="withPopperTop"
                      class="w-100"
                    />
                  </div>
                </template>
                <template #cell(comment)="data">
                  <div
                    class="planning-comment-cell"
                    :class="{ 'planning-comment-cell--pending-delete': commentEndsWithDeletionMarker(data.item.comment) }"
                  >
                    <template v-if="data.item.is_extra">
                      <b-form-checkbox
                        v-model="data.item.should_add_comment"
                        class="mb-50"
                        :disabled="isPlanningRowCommentDisabled(data.item)"
                      >
                        {{ $t('planning.add_comment') }}
                      </b-form-checkbox>
                      <b-form-textarea
                        v-if="data.item.should_add_comment"
                        :value="planningCommentDisplayValue(data.item)"
                        rows="2"
                        :disabled="isPlanningRowCommentDisabled(data.item)"
                        :placeholder="$t('planning.comment_placeholder')"
                        @input="(value) => onPlanningCommentFieldInput(data.item, value)"
                      />
                    </template>
                    <template v-else>
                      <b-form-textarea
                        :value="planningCommentDisplayValue(data.item)"
                        rows="2"
                        :disabled="isPlanningRowCommentDisabled(data.item)"
                        :placeholder="$t('planning.comment_placeholder')"
                        @input="(value) => onPlanningCommentFieldInput(data.item, value)"
                      />
                    </template>
                  </div>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    v-if="statusCode === 'draft'"
                    variant="link"
                    size="sm"
                    class="p-0 text-danger"
                    :style="commentEndsWithDeletionMarker(data.item.comment) ? { textDecoration: 'line-through', color: '#dc3545' } : null"
                    :disabled="itemsSaving || isPlanningRowFieldsDisabled(data.item) || !canUpdatePlanning || !isTrashActionAllowed"
                    @click="onTrashClick(data.index)"
                  >
                    <feather-icon icon="TrashIcon" size="16" />
                  </b-button>
                </template>
            </b-table>
            </div>
            </template>
            <div
              v-if="statusChangeCommentsWithText.length"
              class="mt-2 planning-status-change-history"
            >
              <h6 class="text-capitalize mb-1">{{ $t('planning.status_change_history_title') }}</h6>
              <ul class="list-unstyled mb-0">
                <li
                  v-for="row in statusChangeCommentsWithText"
                  :key="row.id"
                  class="planning-status-change-history__item"
                >
                  <div v-if="row.createdAtFormatted" class="planning-status-change-history__meta small">{{ row.createdAtFormatted }}</div>
                  <div class="planning-status-change-history__user"><strong>{{ row.changedByLabel }}</strong></div>
                  <div class="planning-status-change-history__transition">{{ $t('planning.status_change_transition', { from: row.fromStatusLabel, to: row.toStatusLabel }) }}</div>
                  <div class="planning-status-change-history__comment mt-50">{{ row.comment }}</div>
                </li>
              </ul>
            </div>
          </template>
        </b-card-body>
      </b-card>

      <template v-if="isCreateMode">
        <b-row class="mt-3 planning-form-actions-row">
          <b-col cols="12" class="planning-form-actions-col">
            <div class="d-flex align-items-center flex-wrap">
              <b-button
                v-if="canCreatePlanning"
                variant="primary"
                :disabled="createSubmitting || !activityType || !hasSelectedRowsCreate || !canCreatePlanning"
                @click="onSubmitCreate"
              >
                {{ $t('planning.start_programming') }}
              </b-button>
            </div>
            <b-button variant="outline-secondary" @click="goBack">
              {{ $t('planning.exit') }}
            </b-button>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <b-row class="mt-3 planning-form-actions-row">
          <b-col cols="12" class="planning-form-actions-col">
            <div class="d-flex align-items-center flex-wrap planning-form-actions-left">
              <b-button
                v-if="shouldShowSavePlanningItemsButton"
                variant="primary"
                size="sm"
                class="mr-2 mb-1"
                :disabled="itemsSaving || workflowLoading"
                @click="onUnifiedSavePlanningItems"
              >
                {{ $t('actions.save') }}
              </b-button>
              <v-select
                v-if="showSendToReviewBlock"
                v-model="selectedReviewerId"
                :options="reviewerOptions"
                label="name"
                :filter-by="filterUsersByNameEmailAndUser"
                :reduce="(u) => u.value"
                :clearable="true"
                :disabled="workflowLoading"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :placeholder="$t('planning.select_reviewer')"
                class="reviewer-select mr-2 mb-1"
                style="min-width: 420px"
                append-to-body
                :calculate-position="withPopperTop"
              />
              <b-button
                v-if="showSendToReviewBlock"
                variant="warning"
                size="sm"
                class="mr-2 mb-1"
                :disabled="workflowLoading || !selectedReviewerId"
                @click="onSendToReview"
              >
                {{ $t('planning.send_to_review') }}
              </b-button>
              <v-select
                v-if="showApproveRejectBlock"
                v-model="selectedSecondApproverId"
                :options="approverOptions"
                label="name"
                :filter-by="filterUsersByNameEmailAndUser"
                :reduce="(u) => u.value"
                :clearable="true"
                :disabled="workflowLoading"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :placeholder="$t('planning.select_approver')"
                class="reviewer-select mr-2 mb-1"
                style="min-width: 420px"
                append-to-body
                :calculate-position="withPopperTop"
              />
              <b-button
                v-if="showApproveRejectBlock"
                variant="primary"
                size="sm"
                class="mr-2 mb-1"
                :disabled="workflowLoading || !selectedSecondApproverId"
                @click="onSendToSecondApproval"
              >
                {{ $t('planning.send_second_approval') }}
              </b-button>
              <b-button
                v-if="showApproveRejectBlock || showSecondApprovalAcceptBlock"
                variant="danger"
                class="mr-2 mb-1"
                :disabled="workflowLoading"
                @click="showRejectModal = true"
              >
                {{ $t('planning.reject') }}
              </b-button>
              <b-button
                v-if="showSecondApprovalAcceptBlock"
                variant="success"
                size="sm"
                class="mr-2 mb-1"
                :disabled="workflowLoading"
                @click="onAcceptProgramming"
              >
                {{ $t('planning.accept_programming') }}
              </b-button>
              <b-button
                v-if="showSendProgrammingBlock"
                variant="primary"
                class="mr-2 mb-1"
                :disabled="workflowLoading"
                @click="onSendProgramming"
              >
                {{ $t('planning.send_programming') }}
              </b-button>
            </div>
            <b-button variant="outline-secondary" class="mb-1" @click="goBack">
              {{ $t('planning.exit') }}
            </b-button>
          </b-col>
        </b-row>
        <b-modal v-model="showRejectModal" :title="$t('planning.reject')" @ok="onRejectConfirm" @hidden="rejectComment = ''">
          <b-form-group :label="$t('planning.reject_comment')" label-for="reject-comment">
            <b-form-textarea
              id="reject-comment"
              v-model="rejectComment"
              rows="3"
              :placeholder="$t('planning.reject_comment_placeholder')"
              required
            />
          </b-form-group>
        </b-modal>
      </template>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BFormInput,
  BFormCheckbox,
  BFormGroup,
  BFormTextarea,
  BModal,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ref, computed, watch, onMounted, nextTick } from '@vue/composition-api'
import { useRouter } from '@core/utils/utils'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import axios from '@/libs/axios'
import popperHelper from '@/libs/popper-helper'
import BreadcrumbMenu from '@/components/BreadcrumbMenu.vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import PlanningStatusBadge from './components/PlanningStatusBadge.vue'
import UnitsSelectPaginated from '@/components/UnitsSelectPaginated.vue'
import { getPlanningStatusCode } from './usePlanningStatus'
import { buildUniqueWorkflowUserSelectOptions } from './planningWorkflowUserOptions'
import { fetchPlanningFirstReviewersByActivityType } from './fetchPlanningFirstReviewersByActivityType'
import {
  filterMainUnitsIfPresent,
  hasMainUnitFlag,
  isMainUnit,
  shouldFilterMainUnitsByActivityType,
} from '@/libs/planning/unitFilterByActivityType'

const ACTIVITY_TYPE_SLUGS = [
  { value: 'risk', labelKey: 'planning.evaluacion_riesgo' },
  { value: 'operational_procedure', labelKey: 'planning.ejercicio_operativo' },
  { value: 'simulacrum', labelKey: 'planning.simulacro' },
  { value: 'security_self_audit', labelKey: 'planning.auto_evaluacion_seguridad' },
]

const ACTIVITY_TYPE_API_TO_SLUG = {
  'planning.simulacro': 'simulacrum',
  'Planning.Simulacro': 'simulacrum',
  'planning.evaluacion_riesgo': 'risk',
  'planning.ejercicio_operativo': 'operational_procedure',
  'planning.auto_evaluacion_seguridad': 'security_self_audit',
}

const ACTIVITY_TYPE_LABEL_FALLBACKS = {
  'planning.evaluacion_riesgo': 'Evaluación de riesgo',
  'planning.ejercicio_operativo': 'Ejercicio operativo',
  'planning.simulacro': 'Simulacro',
  'planning.auto_evaluacion_seguridad': 'Auto evaluación de seguridad',
}

const ORIGIN_SECTOR = 'sector'
const ORIGIN_REGION = 'region'
const ORIGIN_UNIT = 'unit'

function resolveCreateOriginFromProvenance(sectorId, regionId) {
  const sectorNumeric = toNumberOrNull(sectorId)
  const regionNumeric = toNumberOrNull(regionId)
  if (sectorNumeric != null && regionNumeric != null) return ORIGIN_SECTOR
  if (sectorNumeric != null) return ORIGIN_SECTOR
  if (regionNumeric != null) return ORIGIN_REGION
  return ORIGIN_UNIT
}

function getOriginForUnitId(unitId, sectorIdSet, regionIdSet) {
  const sectorSet = sectorIdSet instanceof Set ? sectorIdSet : new Set()
  const regionSet = regionIdSet instanceof Set ? regionIdSet : new Set()
  if (sectorSet.has(unitId)) return ORIGIN_SECTOR
  if (regionSet.has(unitId)) return ORIGIN_REGION
  return ORIGIN_UNIT
}

function toNumberOrNull(value) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

function buildNormalizedUnitIdSet(iterable) {
  const set = new Set()
  const list = iterable instanceof Set ? [...iterable] : (Array.isArray(iterable) ? iterable : [])
  list.forEach((raw) => {
    const id = toNumberOrNull(raw)
    if (id != null) set.add(id)
  })
  return set
}

function getManualUnitIdsFromSets(selectedIds, sectorSet, regionSet) {
  const sector = sectorSet instanceof Set ? sectorSet : new Set()
  const region = regionSet instanceof Set ? regionSet : new Set()
  const current = Array.isArray(selectedIds) ? selectedIds : []
  return uniqueDefinedNumbers(current.filter((raw) => {
    const id = toNumberOrNull(raw)
    if (id == null) return false
    return !sector.has(id) && !region.has(id)
  }))
}

function computeMergedUnitIds({ sectorIdsSet, regionIdsSet, manualIds, excludedSet }) {
  const sector = sectorIdsSet instanceof Set ? sectorIdsSet : new Set()
  const region = regionIdsSet instanceof Set ? regionIdsSet : new Set()
  const excluded = excludedSet instanceof Set ? excludedSet : new Set()
  const seen = new Set()
  const fromBulkOrdered = []
  ;[...sector, ...region].forEach((raw) => {
    const id = toNumberOrNull(raw)
    if (id == null || seen.has(id)) return
    seen.add(id)
    if (excluded.has(id)) return
    fromBulkOrdered.push(id)
  })
  const manual = uniqueDefinedNumbers(Array.isArray(manualIds) ? manualIds : [])
  return uniqueDefinedNumbers([...fromBulkOrdered, ...manual])
}

function getPlanningRecordOwnerUserId(planning) {
  if (!planning || typeof planning !== 'object') return null
  const createdBy = planning.created_by && typeof planning.created_by === 'object' ? planning.created_by : null
  const orderedCandidates = [
    planning.created_by_id,
    planning.created_by_user_id,
    planning.owner_id,
    createdBy && createdBy.id,
    planning.user_id,
    planning.user && typeof planning.user === 'object' ? planning.user.id : null,
  ]
  const resolved = orderedCandidates.map((candidate) => toNumberOrNull(candidate)).find((id) => id != null)
  return resolved != null ? resolved : null
}

function extractLastReassignedUserId(unit) {
  const candidate = unit && unit.last_reassigned_or_in_progress_user
  if (!candidate || typeof candidate !== 'object') return null
  return toNumberOrNull(candidate.id)
}

function getAssignableUserId({ unit, assignableUserIds }) {
  const userId = extractLastReassignedUserId(unit)
  if (userId == null) return null
  const isAssignableUserIdsSet = assignableUserIds instanceof Set
  if (!isAssignableUserIdsSet || assignableUserIds.size === 0) return null
  if (!assignableUserIds.has(userId)) return null
  return userId
}

function normalizeSearchText(value) {
  if (value == null) return ''
  return String(value).toLowerCase().trim()
}

function buildUserSearchText(user) {
  const candidate = user && typeof user === 'object' ? user : {}
  const searchParts = [
    candidate.name,
    candidate.full_name,
    candidate.first_name,
    candidate.second_name,
    candidate.email,
    candidate.user,
    candidate.username,
  ]
  return searchParts
    .map((value) => normalizeSearchText(value))
    .filter((value) => value.length > 0)
    .join(' ')
}

function mapUserToSelectOption(user) {
  const userId = toNumberOrNull(user && user.id)
  const name = user && user.name ? String(user.name) : ''
  const fullName = user && user.full_name ? String(user.full_name) : ''
  const email = user && user.email ? String(user.email) : ''
  const username = user && (user.user || user.username) ? String(user.user || user.username) : ''
  const displayName = [name, fullName, email, username].filter((value, index, values) => value && values.indexOf(value) === index).join(' | ') || String(userId != null ? userId : '')
  return {
    value: userId != null ? userId : user && user.id,
    name: displayName,
    searchText: buildUserSearchText(user),
  }
}

function getNestedValueByPath(source, path) {
  if (source == null || typeof source !== 'object') return null
  const keys = Array.isArray(path) ? path : []
  let current = source
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index]
    if (current == null || typeof current !== 'object' || !(key in current)) return null
    current = current[key]
  }
  return current
}

function getIdFromKnownPaths(response) {
  const candidatePaths = [
    ['id'],
    ['planning_id'],
    ['planningId'],
    ['data', 'id'],
    ['data', 'planning_id'],
    ['data', 'planningId'],
    ['data', 'data', 'id'],
    ['data', 'data', 'planning_id'],
    ['data', 'data', 'planningId'],
    ['data', 'data', 'data', 'id'],
    ['planning', 'id'],
    ['data', 'planning', 'id'],
  ]
  for (let index = 0; index < candidatePaths.length; index += 1) {
    const value = getNestedValueByPath(response, candidatePaths[index])
    const id = toNumberOrNull(value)
    if (id != null) return id
  }
  return null
}

function getIdFromNestedObject(response) {
  const queue = [response]
  const visited = new Set()
  while (queue.length > 0) {
    const current = queue.shift()
    if (current == null || typeof current !== 'object') continue
    if (visited.has(current)) continue
    visited.add(current)
    const directId = toNumberOrNull(current.id)
    if (directId != null) return directId
    const planningId = toNumberOrNull(current.planning_id)
    if (planningId != null) return planningId
    const camelPlanningId = toNumberOrNull(current.planningId)
    if (camelPlanningId != null) return camelPlanningId
    Object.keys(current).forEach((key) => {
      const value = current[key]
      if (value != null && typeof value === 'object') queue.push(value)
    })
  }
  return null
}

function extractCreatedPlanningId(response) {
  if (response == null || typeof response !== 'object') return null
  const idFromKnownPaths = getIdFromKnownPaths(response)
  if (idFromKnownPaths != null) return idFromKnownPaths
  return getIdFromNestedObject(response)
}

function resolveDownloadFilenameFromHeaders(headers, fallbackName) {
  const fallback = fallbackName || 'planning.xlsx'
  const disposition = headers && (headers['content-disposition'] || headers['Content-Disposition'])
  if (!disposition) return fallback
  const quotedMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (quotedMatch && quotedMatch[1]) return decodeURIComponent(quotedMatch[1])
  const simpleMatch = disposition.match(/filename="?([^"]+)"?/i)
  if (simpleMatch && simpleMatch[1]) return simpleMatch[1]
  return fallback
}

function uniqueDefinedNumbers(values) {
  const set = new Set()
  ;(Array.isArray(values) ? values : []).forEach((v) => {
    const n = toNumberOrNull(v)
    if (n == null) return
    set.add(n)
  })
  return [...set]
}

async function fetchTemplateFormRowsByActivityType(activityType) {
  if (activityType === 'risk') {
    const { data } = await axios.get('/v1/risk_templates')
    return Array.isArray(data.data) ? data.data : []
  }
  if (activityType === 'operational_procedure') {
    const { data } = await axios.get('/v1/operational_procedure_templates', { params: { page: 1, per_page: 500 } })
    return Array.isArray(data.data) ? data.data : []
  }
  if (activityType === 'simulacrum') {
    const { data } = await axios.get('/v1/simulacrum_templates')
    return Array.isArray(data.data) ? data.data : []
  }
  if (activityType === 'security_self_audit') {
    const { data } = await axios.get('/v1/security_self_audit_templates')
    return Array.isArray(data.data) ? data.data : []
  }
  return []
}

function mapPlanningTemplateRowsToSelectOptions(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const id = toNumberOrNull(row && row.id)
    if (id == null) return null
    const rawLabel = row && (row.name != null ? row.name : row.description)
    const label = rawLabel != null && String(rawLabel).trim() ? String(rawLabel).trim() : `Form ${id}`
    return { value: id, label }
  }).filter(Boolean)
}

function normalizeTemplateFormIdsFromPlanningItem(item) {
  if (item == null || typeof item !== 'object') return []
  const raw = item.template_forms != null ? item.template_forms : item.templateForms
  if (raw == null) return []
  if (!Array.isArray(raw)) return []
  const ids = raw.map((entry) => {
    if (entry == null) return null
    if (typeof entry === 'object' && entry.id != null) return toNumberOrNull(entry.id)
    return toNumberOrNull(entry)
  })
  return uniqueDefinedNumbers(ids)
}

function buildTemplateFormsPayload(templateIds) {
  const list = uniqueDefinedNumbers(Array.isArray(templateIds) ? templateIds : [])
  if (!list.length) return null
  return list
}

function getPlanningFromCurrent(current) {
  if (current == null) return null
  const inner = current.data != null ? current.data : current
  if (inner && typeof inner === 'object' && inner !== current && Array.isArray(inner.items)) return inner
  return current
}

const PLANNING_STATUS_I18N_BY_CODE = {
  draft: 'planning.status_draft',
  review: 'planning.status_review',
  additional: 'planning.status_additional',
  approved: 'planning.status_approved',
  rejected: 'planning.status_rejected',
  released: 'planning.status_released',
}

const DELETION_MARKER = '-Eliminado'

function commentEndsWithDeletionMarker(comment) {
  if (comment == null) return false
  return String(comment).trimEnd().endsWith(DELETION_MARKER)
}

function appendDeletionMarkerToComment(existingComment) {
  const t = existingComment != null ? String(existingComment).trim() : ''
  if (commentEndsWithDeletionMarker(t)) return String(existingComment).trimEnd()
  if (!t) return DELETION_MARKER
  return `${t} ${DELETION_MARKER}`
}

function markPlanningEditRowPendingDeletion(row) {
  if (!row) return
  row.pending_deletion_marked = true
  row.comment = appendDeletionMarkerToComment(row.comment)
  if (row.is_extra) row.should_add_comment = true
}

function stripDeletionMarkerSuffix(raw) {
  return String(raw != null ? raw : '').replace(/\s*-Eliminado\s*$/, '').trimEnd()
}

function planningCommentDisplayValue(row) {
  if (!row || row.comment == null) return ''
  const c = String(row.comment)
  if (commentEndsWithDeletionMarker(c)) return stripDeletionMarkerSuffix(c)
  return c
}

function hasPlanningStatusCommentText(comment) {
  if (comment == null) return false
  return String(comment).trim().length > 0
}

function getPlanningChangedByDisplayName(user) {
  if (!user || typeof user !== 'object') return ''
  const full = user.full_name && String(user.full_name).trim()
  if (full) return full
  const name = user.name && String(user.name).trim()
  const login = user.user && String(user.user).trim()
  if (name && login) return `${name} | ${login}`
  if (name) return name
  if (login) return login
  if (user.id != null) return String(user.id)
  return ''
}

function resolvePlanningStatusDisplayLabel(translate, status) {
  if (!status || typeof status !== 'object') return ''
  const code = status.code != null ? String(status.code).trim() : ''
  if (code && PLANNING_STATUS_I18N_BY_CODE[code]) {
    const key = PLANNING_STATUS_I18N_BY_CODE[code]
    const translated = translate(key)
    if (translated && translated !== key) return translated
  }
  if (status.name != null && String(status.name).trim() !== '') return String(status.name).trim()
  return code
}

function getPlanningItems(planning) {
  return Array.isArray(planning && planning.items) ? planning.items : []
}

function planningRawItemSpecifiesIsActive(rawItem) {
  return rawItem != null && typeof rawItem === 'object' && Object.prototype.hasOwnProperty.call(rawItem, 'is_active')
}

function applyLocalSelectionWhenServerOmitsIsActive(row, rawItems, prevRow) {
  const unitId = row.unit_id
  if (unitId == null || !prevRow) return row
  const raw = rawItems.find((it) => toNumberOrNull(it && it.unit_id) === toNumberOrNull(unitId))
  if (planningRawItemSpecifiesIsActive(raw)) return row
  return { ...row, selected: prevRow.selected === true }
}

function resolveUnitFromItem(item) {
  if (!item) return null
  if (item.unit && typeof item.unit === 'object') return item.unit
  if (item.unit_id != null && typeof item.unit_id === 'object') return item.unit_id
  return null
}

function extractSectorIdsFromPlanningItems(planning) {
  const values = getPlanningItems(planning).map((item) => (item && item.sector_id != null ? item.sector_id : null))
  return uniqueDefinedNumbers(values)
}

function extractRegionIdsFromPlanningItems(planning) {
  const values = getPlanningItems(planning).map((item) => (item && item.region_id != null ? item.region_id : null))
  return uniqueDefinedNumbers(values)
}

function mergeOptionsByValue(primaryOptions, fallbackOptions) {
  const result = []
  const seen = new Set()
  ;(Array.isArray(primaryOptions) ? primaryOptions : []).forEach((option) => {
    if (!option) return
    const value = option.value != null ? option.value : option.id
    if (value == null || seen.has(value)) return
    seen.add(value)
    result.push(option)
  })
  ;(Array.isArray(fallbackOptions) ? fallbackOptions : []).forEach((option) => {
    if (!option) return
    const value = option.value != null ? option.value : option.id
    if (value == null || seen.has(value)) return
    seen.add(value)
    result.push(option)
  })
  return result
}

function buildFallbackSectorOptionsFromPlanning(planning) {
  const map = new Map()
  getPlanningItems(planning).forEach((item) => {
    const unit = item && (item.unit || (item.unit_id && typeof item.unit_id === 'object' ? item.unit_id : null))
    const sector = unit && unit.sector
    const id = sector && sector.id != null ? toNumberOrNull(sector.id) : null
    const name = sector && sector.name != null ? String(sector.name) : ''
    if (id == null || !name) return
    if (!map.has(id)) map.set(id, name)
  })
  return [...map.entries()].map(([value, label]) => ({ value, label }))
}

function buildFallbackRegionOptionsFromPlanning(planning) {
  const map = new Map()
  getPlanningItems(planning).forEach((item) => {
    const unit = item && (item.unit || (item.unit_id && typeof item.unit_id === 'object' ? item.unit_id : null))
    const region = unit && unit.region
    const id = region && region.id != null ? toNumberOrNull(region.id) : null
    const name = region && region.name != null ? String(region.name) : ''
    if (id == null || !name) return
    if (!map.has(id)) map.set(id, name)
  })
  return [...map.entries()].map(([value, label]) => ({ value, label }))
}

function normalizeActivityTypeFromApi(val) {
  if (val == null) return null
  const stringVal = String(val).trim()
  if (ACTIVITY_TYPE_SLUGS.some((o) => o.value === stringVal)) return stringVal
  return ACTIVITY_TYPE_API_TO_SLUG[stringVal] ?? ACTIVITY_TYPE_API_TO_SLUG[stringVal.toLowerCase()] ?? stringVal
}

function getAcceptedAtDate(unit) {
  const acceptedAt = unit && unit.last_accepted_risk_evaluation && unit.last_accepted_risk_evaluation.accepted_at
  if (!acceptedAt) return '-'
  return moment(acceptedAt).format('DD/MM/YYYY')
}

function normalizeRiskLevel(value) {
  if (!value) return ''
  const raw = String(value).trim().toLowerCase()
  if (!raw) return ''
  const compact = raw.replace(/\s+/g, ' ')
  if (compact === 'medio bajo') return 'medio-bajo'
  if (compact === 'medio alto') return 'medio-alto'
  return compact.replace('–', '-')
}

function formatRiskLevel(value) {
  const normalized = normalizeRiskLevel(value)
  if (!normalized) return '-'
  return normalized
    .split('-')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join('-')
}

function getResidualRiskLevel(unit) {
  const level = unit && unit.threats_summary && unit.threats_summary.global && unit.threats_summary.global.level
  if (!level) return '-'
  return formatRiskLevel(level)
}

function getRiskCadence(normalizedLevel) {
  if (!normalizedLevel) return { monthsToAdd: 0, yearsToAdd: 0 }
  if (normalizedLevel === 'alto') return { monthsToAdd: 6, yearsToAdd: 0 }
  if (normalizedLevel === 'medio-alto') return { monthsToAdd: 0, yearsToAdd: 1 }
  if (normalizedLevel === 'medio') return { monthsToAdd: 0, yearsToAdd: 2 }
  if (normalizedLevel === 'medio-bajo') return { monthsToAdd: 0, yearsToAdd: 3 }
  if (normalizedLevel === 'bajo') return { monthsToAdd: 0, yearsToAdd: 4 }
  return { monthsToAdd: 0, yearsToAdd: 0 }
}

function computeRiskWindow(acceptedAt, normalizedLevel, now) {
  if (!acceptedAt || !normalizedLevel) return { start: '', end: '' }
  const { monthsToAdd, yearsToAdd } = getRiskCadence(normalizedLevel)
  const dueDate = moment(acceptedAt).add(monthsToAdd, 'months').add(yearsToAdd, 'years')
  if (!dueDate.isValid()) return { start: '', end: '' }
  const start = dueDate.clone().startOf('month')
  const end = dueDate.clone().endOf('month')
  if (end.isBefore(now, 'day')) return { start: '', end: '' }
  return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') }
}

function extractFirstUnitFromRiskResponse(responseData) {
  const root = responseData && responseData.data ? responseData.data : responseData
  const inner = root && root.data ? root.data : root
  if (Array.isArray(inner)) return inner[0] || null
  if (inner && Array.isArray(inner.data)) return inner.data[0] || null
  return null
}

function getPlanningUnitDeactivation(rawUnit) {
  if (!rawUnit || typeof rawUnit !== 'object') return null
  const unit = rawUnit && rawUnit.unit && typeof rawUnit.unit === 'object' ? rawUnit.unit : rawUnit
  const candidates = [
    rawUnit.deactivation,
    rawUnit.router && rawUnit.router.deactivation,
    unit && unit.deactivation,
    unit && unit.router && unit.router.deactivation,
  ]
  const found = candidates.find((value) => value != null)
  return found != null ? found : null
}

function isPlanningUnitDisabled(rawUnit) {
  return getPlanningUnitDeactivation(rawUnit) != null
}

function filterActivePlanningUnits(units) {
  const list = Array.isArray(units) ? units : []
  return list.filter((unit) => !isPlanningUnitDisabled(unit))
}

function applyRiskWindowToCreateRow(row, activityType) {
  if (!row || activityType !== 'risk') return row
  const hasStart = row.start_date != null && String(row.start_date).trim() !== ''
  const hasEnd = row.end_date != null && String(row.end_date).trim() !== ''
  if (hasStart || hasEnd) return row
  const start = row.risk_window_start || ''
  const end = row.risk_window_end || ''
  return {
    ...row,
    start_date: start || null,
    end_date: end || null,
  }
}

function applyRiskWindowToEditableItem(item, activityType) {
  if (!item || activityType !== 'risk') return item
  const hasStart = item.start_date != null && String(item.start_date).trim() !== ''
  const hasEnd = item.end_date != null && String(item.end_date).trim() !== ''
  if (hasStart || hasEnd) return item
  const start = item.risk_window_start || ''
  const end = item.risk_window_end || ''
  return {
    ...item,
    start_date: start || '',
    end_date: end || '',
  }
}

function normalizeDateInput(value) {
  if (!value) return ''
  return String(value).slice(0, 10)
}

function hasValidDateOrder(startDate, endDate) {
  const normalizedStartDate = normalizeDateInput(startDate)
  const normalizedEndDate = normalizeDateInput(endDate)
  if (!normalizedStartDate || !normalizedEndDate) return false
  const startMoment = moment(normalizedStartDate, 'YYYY-MM-DD', true)
  const endMoment = moment(normalizedEndDate, 'YYYY-MM-DD', true)
  if (!startMoment.isValid() || !endMoment.isValid()) return false
  return endMoment.isAfter(startMoment, 'day')
}

function isAssignedUserValid(value) {
  return toNumberOrNull(value) != null
}

function isEditableItemReadyForReview(item) {
  if (!item || typeof item !== 'object') return false
  if (!isAssignedUserValid(item.user_id)) return false
  return hasValidDateOrder(item.start_date, item.end_date)
}

function getReviewPendingCounts(items) {
  const rows = Array.isArray(items) ? items : []
  const activeRows = rows.filter((item) => item && item.selected === true)
  if (rows.length > 0 && activeRows.length === 0) {
    return {
      incompleteItemsCount: 1,
      missingAssignedUserCount: 0,
      missingDatesCount: 0,
      invalidDateOrderCount: 0,
      noActiveRows: true,
    }
  }
  return activeRows.reduce((accumulator, item) => {
    if (commentEndsWithDeletionMarker(item && item.comment)) return accumulator
    const hasAssignedUser = isAssignedUserValid(item && item.user_id)
    const hasStartDate = normalizeDateInput(item && item.start_date) !== ''
    const hasEndDate = normalizeDateInput(item && item.end_date) !== ''
    const hasDates = hasStartDate && hasEndDate
    const hasValidOrder = hasValidDateOrder(item && item.start_date, item && item.end_date)
    if (!hasAssignedUser || !hasDates || !hasValidOrder) accumulator.incompleteItemsCount += 1
    if (!hasAssignedUser) accumulator.missingAssignedUserCount += 1
    if (!hasDates) accumulator.missingDatesCount += 1
    if (hasDates && !hasValidOrder) accumulator.invalidDateOrderCount += 1
    return accumulator
  }, {
    incompleteItemsCount: 0,
    missingAssignedUserCount: 0,
    missingDatesCount: 0,
    invalidDateOrderCount: 0,
  })
}

function getPlanningEditTableRowClass(item, type) {
  if (type !== 'row' || !item) return null
  if (item.selected === true) return null
  return 'planning-row-inactive'
}

function buildIncompleteEditableItemsToastProps(pending) {
  if (pending.noActiveRows) {
    return {
      title: 'Active al menos un inmueble',
      text: 'Marque la casilla de al menos un inmueble para incluirlo en el flujo.',
      icon: 'AlertTriangleIcon',
      variant: 'warning',
    }
  }
  const detailParts = []
  if (pending.missingDatesCount > 0) detailParts.push(`${pending.missingDatesCount} con fechas incompletas`)
  if (pending.invalidDateOrderCount > 0) detailParts.push(`${pending.invalidDateOrderCount} con fecha fin menor o igual a fecha inicio`)
  if (pending.missingAssignedUserCount > 0) detailParts.push(`${pending.missingAssignedUserCount} sin asignado`)
  return {
    title: `Faltan ${pending.incompleteItemsCount} inmuebles por completar`,
    text: detailParts.join(', '),
    icon: 'AlertTriangleIcon',
    variant: 'warning',
  }
}

const TABLE_FIELDS = [
  { key: 'selected', label: '' },
  { key: 'unit_name', label: 'planning.property_name' },
  { key: 'category_name', label: 'planning.property_type' },
  { key: 'start_date', label: 'planning.fecha_inicio' },
  { key: 'end_date', label: 'planning.fecha_fin' },
  { key: 'assigned_to_id', label: 'planning.assigned_to' },
  { key: 'actions', label: 'actions.name' },
]

const EDITABLE_ITEMS_TABLE_FIELDS = [
  { key: 'selected', label: '' },
  { key: 'unit_name', label: 'planning.property_name' },
  { key: 'category_name', label: 'planning.property_type' },
  { key: 'start_date', label: 'planning.fecha_inicio' },
  { key: 'end_date', label: 'planning.fecha_fin' },
  { key: 'user_id', label: 'planning.assigned_to' },
  { key: 'actions', label: ' ' },
]

function buildCreateTableFields(activityType) {
  if (activityType !== 'risk') return TABLE_FIELDS
  return [
    { key: 'selected', label: '' },
    { key: 'unit_name', label: 'planning.property_name' },
    { key: 'category_name', label: 'planning.property_type' },
    { key: 'last_sra_date', label: 'planning.last_sra_date' },
    { key: 'residual_risk_level', label: 'planning.residual_risk_level' },
    { key: 'start_date', label: 'planning.fecha_inicio' },
    { key: 'end_date', label: 'planning.fecha_fin' },
    { key: 'assigned_to_id', label: 'planning.assigned_to' },
    { key: 'actions', label: 'actions.name' },
  ]
}

function buildEditTableFields(activityType) {
  if (activityType !== 'risk') return EDITABLE_ITEMS_TABLE_FIELDS
  return [
    { key: 'selected', label: '' },
    { key: 'unit_name', label: 'planning.property_name' },
    { key: 'category_name', label: 'planning.property_type' },
    { key: 'last_sra_date', label: 'planning.last_sra_date' },
    { key: 'residual_risk_level', label: 'planning.residual_risk_level' },
    { key: 'start_date', label: 'planning.fecha_inicio' },
    { key: 'end_date', label: 'planning.fecha_fin' },
    { key: 'user_id', label: 'planning.assigned_to' },
    { key: 'actions', label: ' ' },
  ]
}

function formatDate(value) {
  if (!value) return '-'
  return moment(value).format('DD/MM/YYYY')
}

function getUnitCategoryName(unit) {
  if (!unit) return '-'
  const fc = unit.facility_category
  if (fc && fc.name) return fc.name
  if (unit.category && unit.category.name) return unit.category.name
  return '-'
}

function normalizePlanningPropertyCategoryLabel(row) {
  const raw = row && row.category_name != null ? String(row.category_name).trim() : ''
  if (!raw || raw === '-') return null
  return raw
}

function collectDistinctPlanningPropertyTypes(rows) {
  const names = new Set()
  for (const row of rows || []) {
    const label = normalizePlanningPropertyCategoryLabel(row)
    if (label == null) continue
    names.add(label)
  }
  return [...names].sort((a, b) => a.localeCompare(b))
}

function reducePlanningPropertyTypeOption(item) {
  if (item == null) return item
  return item.value != null ? item.value : item
}

function buildReviewExtraItemFromUnit({ unit, activityType }) {
  const unitId = unit && unit.id != null ? unit.id : unit && unit.value != null ? unit.value : null
  if (unitId == null) return null
  const acceptedAt = unit && unit.last_accepted_risk_evaluation && unit.last_accepted_risk_evaluation.accepted_at
  const normalizedLevel = normalizeRiskLevel(unit && unit.threats_summary && unit.threats_summary.global && unit.threats_summary.global.level)
  const riskWindow = computeRiskWindow(acceptedAt, normalizedLevel, moment())
  return {
    unit_id: unitId,
    unit_name: (unit && (unit.description || unit.name)) || '-',
    category_name: getUnitCategoryName(unit),
    last_sra_date: getAcceptedAtDate(unit),
    residual_risk_level: getResidualRiskLevel(unit),
    start_date: activityType === 'risk' ? (riskWindow.start || '') : '',
    end_date: activityType === 'risk' ? (riskWindow.end || '') : '',
    user_id: null,
    comment: '',
    should_add_comment: false,
    pending_deletion_marked: false,
    is_extra: true,
    selected: true,
    origin: ORIGIN_UNIT,
    sector_id: null,
    region_id: null,
  }
}

function mapEditablePlanningRowToItemPayload(item) {
  if (!item || item.unit_id == null) return null
  const payloadItem = { unit_id: item.unit_id }
  if (item.id != null) payloadItem.id = item.id
  if (item.start_date && String(item.start_date).trim().length >= 10) payloadItem.start_date = String(item.start_date).slice(0, 10)
  if (item.end_date && String(item.end_date).trim().length >= 10) payloadItem.target_date = String(item.end_date).slice(0, 10)
  payloadItem.user_id = toNumberOrNull(item.user_id)
  payloadItem.origin = item.origin ?? null
  payloadItem.sector_id = item.sector_id ?? null
  payloadItem.region_id = item.region_id ?? null
  payloadItem.is_active = item.selected === true
  const comment = item.comment != null ? String(item.comment).trim() : ''
  if (comment) payloadItem.comment = comment
  return payloadItem
}

function mapReviewExtraItemToUpdatePayload(item) {
  const payloadItem = { unit_id: item && item.unit_id != null ? item.unit_id : null }
  if (payloadItem.unit_id == null) return null
  if (item && item.id != null) payloadItem.id = item.id
  const startDate = item && item.start_date ? String(item.start_date).slice(0, 10) : ''
  const targetDate = item && item.end_date ? String(item.end_date).slice(0, 10) : ''
  if (startDate) payloadItem.start_date = startDate
  if (targetDate) payloadItem.target_date = targetDate
  payloadItem.user_id = toNumberOrNull(item && item.user_id)
  payloadItem.origin = ORIGIN_UNIT
  payloadItem.sector_id = null
  payloadItem.region_id = null
  const extraComment = item && item.comment != null ? String(item.comment).trim() : ''
  if (extraComment && (item.should_add_comment || item.pending_deletion_marked)) {
    payloadItem.comment = extraComment
  }
  payloadItem.is_active = item && item.selected === true
  return payloadItem
}

function buildEditableItems(planning) {
  const raw = planning && planning.items
  let items = Array.isArray(raw) ? raw : []
  try {
    items = JSON.parse(JSON.stringify(items))
  } catch (_) {}
  return items.map((it) => {
    const unit = it.unit && typeof it.unit === 'object' ? it.unit : null
    const unitId = it.unit_id ?? unit?.id ?? null
    const unitName = (unit && (unit.description || unit.name)) ? String(unit.description || unit.name).trim() : (unitId != null ? 'Unit ' + unitId : '-')
    const categoryName = getUnitCategoryName(unit)
    const lastSraDate = getAcceptedAtDate(unit)
    const residualRiskLevel = getResidualRiskLevel(unit)
    const rawUserId = it.user_id ?? (it.user && typeof it.user === 'object' ? it.user.id : null) ?? null
    const userId = rawUserId != null ? Number(rawUserId) : null
    const startDate = it.start_date ? String(it.start_date).slice(0, 10) : ''
    const endDate = it.target_date ? String(it.target_date).slice(0, 10) : (it.end_date ? String(it.end_date).slice(0, 10) : '')
    const commentText = it.comment != null ? String(it.comment) : ''
    const isActiveFromApi = it.is_active
    const selectedFromApi = isActiveFromApi === undefined || isActiveFromApi === null ? false : Boolean(isActiveFromApi)
    return {
      id: it.id,
      unit_id: unitId,
      unit_name: unitName,
      category_name: categoryName,
      last_sra_date: lastSraDate,
      residual_risk_level: residualRiskLevel,
      start_date: startDate,
      end_date: endDate,
      user_id: userId,
      comment: commentText,
      pending_deletion_marked: commentEndsWithDeletionMarker(commentText),
      selected: selectedFromApi,
      origin: it.origin ?? null,
      sector_id: it.sector_id ?? null,
      region_id: it.region_id ?? null,
    }
  })
}

function canPlanningAction(vm, action) {
  if (!vm || typeof vm.$can !== 'function') return true
  return vm.$can(action, 'Planning')
}

export default {
  name: 'PlanningForm',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BFormInput,
    BFormCheckbox,
    BFormGroup,
    BFormTextarea,
    BModal,
    BSpinner,
    BTooltip,
    vSelect,
    BreadcrumbMenu,
    FeatherIcon,
    PlanningStatusBadge,
    UnitsSelectPaginated,
  },
  setup(_, { root }) {
    const { route, router } = useRouter()
    const vm = root
    const toast = useToast()
    const loading = ref(false)
    const loadError = ref(false)
    const createSubmitting = ref(false)
    const workflowLoading = ref(false)
    const itemsSaving = ref(false)
    const selectedReviewerId = ref(null)
    const selectedSecondApproverId = ref(null)
    const showRejectModal = ref(false)
    const rejectComment = ref('')
    const editableItems = ref([])
    const showAddExtraUnits = ref(false)
    const selectedExtraUnitIds = ref([])
    const reviewExtraItems = ref([])
    const workflowReviewerUsers = ref([])
    const workflowApproverUsers = ref([])

    const activityType = ref(null)
    const sectorIds = ref([])
    const regionIds = ref([])
    const selectedUnitIds = ref([])
    const tableRows = ref([])
    const lastSectorUnitIds = ref(new Set())
    const lastRegionUnitIds = ref(new Set())
    const lastSectorUnits = ref([])
    const lastRegionUnits = ref([])
    const bulkExcludedUnitIds = ref(new Set())
    const isSuppressingSectorRegionBulkSync = ref(false)
    const lastManualUnits = ref([])
    const templateFormIds = ref([])
    const templateFormOptions = ref([])
    const templateFormsLoading = ref(false)
    const mainUnitByUnitIdCache = new Map()
    const shouldFilterMainUnits = computed(() =>
      shouldFilterMainUnitsByActivityType(activityType.value)
    )

    function filterUnitsByMainUnit(units) {
      const list = Array.isArray(units) ? units : []
      if (!shouldFilterMainUnits.value) return list
      return filterMainUnitsIfPresent(list)
    }

    function isAllowedUnitByMainUnit(unit) {
      if (!shouldFilterMainUnits.value) return true
      if (!hasMainUnitFlag(unit)) return true
      return isMainUnit(unit)
    }

    function extractUnitIdFromPlanningItem(unit) {
      if (!unit || typeof unit !== 'object') return null
      const rawValue = unit.id != null
        ? unit.id
        : unit.value != null
          ? unit.value
          : unit.unit && unit.unit.id != null
            ? unit.unit.id
            : unit.unit_id && typeof unit.unit_id === 'object' && unit.unit_id.id != null
              ? unit.unit_id.id
              : unit.unit_id
      return toNumberOrNull(rawValue)
    }

    async function isMainUnitByUnitId(unitId) {
      const normalizedUnitId = toNumberOrNull(unitId)
      if (normalizedUnitId == null) return false
      if (mainUnitByUnitIdCache.has(normalizedUnitId)) {
        return mainUnitByUnitIdCache.get(normalizedUnitId) === true
      }
      try {
        const { data } = await axios.get(`/v1/unit/${normalizedUnitId}`)
        const unit = (data && data.data) || data
        const result = isMainUnit(unit)
        mainUnitByUnitIdCache.set(normalizedUnitId, result)
        return result
      } catch (_) {
        mainUnitByUnitIdCache.set(normalizedUnitId, false)
        return false
      }
    }

    async function filterUnitsByMainUnitWithFallback(units) {
      const list = Array.isArray(units) ? units : []
      if (!shouldFilterMainUnits.value) return list
      const filteredByInlineFlag = filterUnitsByMainUnit(list)
      const hasInlineMainUnitFlag = list.some((unit) => hasMainUnitFlag(unit))
      if (hasInlineMainUnitFlag) return filteredByInlineFlag
      const checks = await Promise.all(
        list.map(async (unit) => {
          const unitId = extractUnitIdFromPlanningItem(unit)
          if (unitId == null) return null
          const allowed = await isMainUnitByUnitId(unitId)
          return allowed ? unit : null
        })
      )
      return checks.filter((unit) => unit != null)
    }

    const planningId = computed(() => route.value && route.value.params && route.value.params.id)
    const isCreateMode = computed(() => !planningId.value)
    const canReadPlanning = computed(() => canPlanningAction(vm, 'read'))
    const canCreatePlanning = computed(() => canPlanningAction(vm, 'create'))
    const canUpdatePlanning = computed(() => canPlanningAction(vm, 'update'))
    const canDeletePlanning = computed(() => canPlanningAction(vm, 'delete'))
    const canSubmitReviewPlanning = computed(() => canPlanningAction(vm, 'submit_review'))
    const canApproveAdditionalPlanning = computed(() => canPlanningAction(vm, 'approve_additional'))
    const canApprovePlanning = computed(() => canPlanningAction(vm, 'approve_planning'))
    const areDependentFieldsDisabled = computed(
      () => isCreateMode.value && (!canCreatePlanning.value || activityType.value == null || activityType.value === '')
    )
    const currentItem = computed(() => store.getters['planning/getCurrent'])
    const currentPlanning = computed(() => getPlanningFromCurrent(currentItem.value))
    const currentUserId = computed(() => {
      const user = store.getters['user/getUser'] || store.state.user.user || null
      return toNumberOrNull(user && user.id)
    })
    const statusCode = computed(() => getPlanningStatusCode(currentPlanning.value))
    const isDivisionRegionUnitsSelectionLocked = computed(
      () => !isCreateMode.value && statusCode.value !== 'draft'
    )
    const isReleasedStatus = computed(() => statusCode.value === 'released')
    const isEditLocked = computed(() => isReleasedStatus.value || !canUpdatePlanning.value)
    const isCurrentUserPlanningRecordOwner = computed(() => {
      const ownerId = getPlanningRecordOwnerUserId(currentPlanning.value)
      const uid = currentUserId.value
      if (ownerId == null || uid == null) return false
      return ownerId === uid
    })
    const isDraftAssigneeGateActive = computed(() => statusCode.value === 'draft' && canUpdatePlanning.value)
    const isDraftPlanningEditBlocked = computed(
      () => isDraftAssigneeGateActive.value && !isCurrentUserPlanningRecordOwner.value
    )
    const isPlanningHeaderOrTableShellDisabled = computed(
      () => isEditLocked.value || isDraftPlanningEditBlocked.value
    )
    const isTrashActionAllowed = computed(() => statusCode.value === 'draft')
    const showSendToReviewBlock = computed(
      () =>
        statusCode.value === 'draft'
        && canCreatePlanning.value
        && canSubmitReviewPlanning.value
        && canUpdatePlanning.value
        && isCurrentUserPlanningRecordOwner.value
    )
    const showApproveRejectBlock = computed(() => statusCode.value === 'review' && canApproveAdditionalPlanning.value)
    const showSecondApprovalAcceptBlock = computed(() => statusCode.value === 'additional' && canApprovePlanning.value)
    const showSendProgrammingBlock = computed(() => statusCode.value === 'approved' && canCreatePlanning.value)
    const isReviewMode = computed(() => statusCode.value === 'review')
    const isReviewFlowMode = computed(() => statusCode.value === 'review' || statusCode.value === 'additional')
    const showEditableItemsTable = computed(() => !isCreateMode.value && !!planningId.value)
    const shouldShowSavePlanningItemsButton = computed(
      () =>
        showEditableItemsTable.value
        && !isReleasedStatus.value
        && canUpdatePlanning.value
        && statusCode.value === 'draft'
        && isCurrentUserPlanningRecordOwner.value
        && !isDraftPlanningEditBlocked.value
    )
    const editModeTableItems = computed(() => {
      if (isReviewFlowMode.value) return [...editableItems.value, ...reviewExtraItems.value]
      return editableItems.value
    })
    const distinctPropertyTypesInPlanningTable = computed(() => {
      const rows = isCreateMode.value ? tableRows.value : editModeTableItems.value
      const filtered = isCreateMode.value
        ? rows
        : rows.filter((row) => !row.pending_deletion_marked)
      return collectDistinctPlanningPropertyTypes(filtered)
    })
    const selectedPropertyTypeLabelsCreate = ref([])
    const selectedPropertyTypeLabelsEdit = ref([])
    const propertyTypeFilterSelectOptions = computed(() =>
      distinctPropertyTypesInPlanningTable.value.map((label) => ({ label, value: label }))
    )

    function syncSelectedPropertyTypeLabelsFromDistinct() {
      const types = distinctPropertyTypesInPlanningTable.value
      const typeSet = new Set(types)
      const merge = (labelsRef) => {
        const kept = labelsRef.value.filter((t) => typeSet.has(t))
        const merged = [...kept]
        for (const t of types) {
          if (!merged.includes(t)) merged.push(t)
        }
        merged.sort((a, b) => a.localeCompare(b))
        labelsRef.value = merged
      }
      if (isCreateMode.value) merge(selectedPropertyTypeLabelsCreate)
      else merge(selectedPropertyTypeLabelsEdit)
    }

    watch(distinctPropertyTypesInPlanningTable, syncSelectedPropertyTypeLabelsFromDistinct, { immediate: true })

    const editRowsForBulkSelectInEditMode = computed(() => {
      const rows = editModeTableItems.value
      if (statusCode.value !== 'draft' || !canUpdatePlanning.value) return rows
      if (!isCurrentUserPlanningRecordOwner.value) return []
      return rows
    })

    const statusChangeCommentsWithText = computed(() => {
      const planning = currentPlanning.value
      const raw = planning && Array.isArray(planning.status_change_comments) ? planning.status_change_comments : []
      const translate = (key) => vm.$t(key)
      const filtered = raw.filter((entry) => entry && hasPlanningStatusCommentText(entry.comment))
      const sorted = [...filtered].sort((a, b) => {
        const timeA = a.created_at ? Date.parse(a.created_at) : 0
        const timeB = b.created_at ? Date.parse(b.created_at) : 0
        return timeA - timeB
      })
      return sorted.map((entry, index) => {
        const createdRaw = entry.created_at
        const createdAtFormatted = createdRaw && moment(createdRaw).isValid()
          ? moment(createdRaw).format('DD/MM/YYYY HH:mm')
          : ''
        const rowId = entry.id != null ? entry.id : `status-comment-${index}-${createdAtFormatted}`
        return {
          id: rowId,
          comment: String(entry.comment).trim(),
          changedByLabel: getPlanningChangedByDisplayName(entry.changed_by),
          fromStatusLabel: resolvePlanningStatusDisplayLabel(translate, entry.from_status),
          toStatusLabel: resolvePlanningStatusDisplayLabel(translate, entry.to_status),
          createdAtFormatted,
        }
      })
    })

    async function loadWorkflowReviewerUsers() {
      try {
        const users = await fetchPlanningFirstReviewersByActivityType(activityType.value)
        workflowReviewerUsers.value = Array.isArray(users) ? users : []
      } catch (_) {
        workflowReviewerUsers.value = []
      }
    }

    async function loadWorkflowApproverUsers() {
      try {
        const users = await store.dispatch('planning/getApproverUsers')
        workflowApproverUsers.value = Array.isArray(users) ? users : []
      } catch (_) {
        workflowApproverUsers.value = []
      }
    }

    async function syncWorkflowUserLists() {
      if (isCreateMode.value) {
        workflowReviewerUsers.value = []
        workflowApproverUsers.value = []
        return
      }
      if (!planningId.value) {
        workflowReviewerUsers.value = []
        workflowApproverUsers.value = []
        return
      }
      if (statusCode.value === 'draft' && showSendToReviewBlock.value) {
        workflowApproverUsers.value = []
        await loadWorkflowReviewerUsers()
        return
      }
      if (statusCode.value === 'review' && showApproveRejectBlock.value) {
        workflowReviewerUsers.value = []
        await loadWorkflowApproverUsers()
        return
      }
      workflowReviewerUsers.value = []
      workflowApproverUsers.value = []
    }

    const activityTypeOptions = computed(() => {
      const t = typeof window !== 'undefined' && window.i18n && typeof window.i18n.t === 'function'
        ? (key) => window.i18n.t(key)
        : (key) => key
      return ACTIVITY_TYPE_SLUGS.map((item) => {
        const translated = t(item.labelKey)
        const label = translated !== item.labelKey ? translated : (ACTIVITY_TYPE_LABEL_FALLBACKS[item.labelKey] || item.labelKey)
        return { value: item.value, label }
      })
    })
    const planningTitle = computed(() => {
      const t = typeof window !== 'undefined' && window.i18n && typeof window.i18n.t === 'function'
        ? (key) => window.i18n.t(key)
        : () => ''
      const text = t('planning.planification')
      return text && text !== 'planning.planification' ? text : 'Planificación'
    })
    const selectedUnitsTitle = computed(() => {
      const t = typeof window !== 'undefined' && window.i18n && typeof window.i18n.t === 'function'
        ? (key) => window.i18n.t(key)
        : () => ''
      const text = t('planning.selected_units')
      return text && text !== 'planning.selected_units' ? text : 'Inmuebles seleccionados'
    })
    const sectorOptions = computed(() => store.getters['sector/getSelectedItems'] || [])
    const regionOptions = computed(() => store.getters['region/getSelectedItems'] || [])
    const userItems = computed(() => store.getters['user/getItems'] || store.getters['user/getSelectedItems'] || [])
    const localSectorOptions = computed(() => {
      const primary = (sectorOptions.value || []).filter((item) => item != null).map((item) => ({
        value: item.value != null ? item.value : item.id,
        label: item.label != null ? item.label : (item.name != null ? item.name : String(item.value != null ? item.value : item.id)),
      }))
      const fallback = buildFallbackSectorOptionsFromPlanning(currentPlanning.value)
      return mergeOptionsByValue(primary, fallback)
    })
    const localRegionOptions = computed(() => {
      const primary = (regionOptions.value || []).filter((item) => item != null).map((item) => ({
        value: item.value != null ? item.value : item.id,
        label: item.label != null ? item.label : (item.name != null ? item.name : String(item.value != null ? item.value : item.id)),
      }))
      const fallback = buildFallbackRegionOptionsFromPlanning(currentPlanning.value)
      return mergeOptionsByValue(primary, fallback)
    })
    const localUserOptions = computed(() =>
      (userItems.value || []).map((user) => mapUserToSelectOption(user))
    )
    const assignableUserIds = computed(() => {
      const ids = (localUserOptions.value || []).map((option) => toNumberOrNull(option && option.value))
      return new Set(ids.filter((id) => id != null))
    })
    const userOptions = computed(() => {
      const fromStore = (userItems.value || []).map((user) => mapUserToSelectOption(user))
      const seenIds = new Set(fromStore.map((u) => u.value))
      const planning = currentPlanning.value
      const items = Array.isArray(planning && planning.items) ? planning.items : []
      items.forEach((it) => {
        const u = it.user || (it.user_id && typeof it.user_id === 'object' ? it.user_id : null)
        if (u && u.id != null) {
          const id = Number(u.id)
          if (!seenIds.has(id)) {
            seenIds.add(id)
            fromStore.push(mapUserToSelectOption(u))
          }
        }
      })
      return fromStore
    })
    const reviewerOptions = computed(() => buildUniqueWorkflowUserSelectOptions(workflowReviewerUsers.value))
    const approverOptions = computed(() => buildUniqueWorkflowUserSelectOptions(workflowApproverUsers.value))

    const tableFields = computed(() => buildCreateTableFields(activityType.value))
    const editableItemsTableFields = computed(() => buildEditTableFields(activityType.value))
    const editableItemsTableFieldsResolved = computed(() => {
      const base = editableItemsTableFields.value
      if (statusCode.value === 'approved' || statusCode.value === 'rejected') return base
      const withoutActions = base.filter((field) => field.key !== 'actions')
      const actionsField = base.find((field) => field.key === 'actions')
      const commentField = { key: 'comment', label: 'planning.comment' }
      return [...withoutActions, commentField, ...(actionsField ? [actionsField] : [])]
    })

    const allSelectedCreate = computed(() => {
      const rows = tableRows.value
      if (!rows.length) return false
      return rows.every((row) => row.selected === true)
    })
    const isIndeterminateCreate = computed(() => {
      const rows = tableRows.value
      if (!rows.length) return false
      const count = rows.filter((row) => row.selected === true).length
      return count > 0 && count < rows.length
    })
    const allSelectedEdit = computed(() => {
      const rows = editRowsForBulkSelectInEditMode.value
      if (!rows.length) return false
      return rows.every((row) => row.selected === true)
    })
    const isIndeterminateEdit = computed(() => {
      const rows = editRowsForBulkSelectInEditMode.value
      if (!rows.length) return false
      const count = rows.filter((row) => row.selected === true).length
      return count > 0 && count < rows.length
    })
    const hasSelectedRowsCreate = computed(() => tableRows.value.some((row) => row.selected === true))
    const reviewPendingCounts = computed(() => getReviewPendingCounts(editModeTableItems.value))
    const reviewSelectedUnitIdSet = computed(() => {
      const ids = editModeTableItems.value.map((row) => toNumberOrNull(row && row.unit_id)).filter((id) => id != null)
      return new Set(ids)
    })

    const unitsInitialOptionsForSelect = computed(() => {
      const seen = new Set()
      const out = []
      const formatLabel = (description, sirh, rems) => {
        const desc = description != null && description !== '' ? String(description) : ''
        const s = sirh != null && sirh !== '' ? String(sirh) : '-'
        const r = rems != null && rems !== '' ? String(rems) : '-'
        const suffix = `SIRH: ${s} | REMS: ${r}`
        return desc ? `${desc} | ${suffix}` : suffix
      }
      ;[...lastSectorUnits.value, ...lastRegionUnits.value, ...lastManualUnits.value].forEach((u) => {
        if (!u || (u.id == null && u.value == null)) return
        const id = u.id != null ? u.id : u.value
        if (seen.has(id)) return
        seen.add(id)
        out.push({
          value: id,
          label: formatLabel(u.description || u.name, u.sirh, u.rems),
        })
      })
      return out
    })
    const reviewExtraUnitsInitialOptions = computed(() =>
      unitsInitialOptionsForSelect.value.filter((option) => !reviewSelectedUnitIdSet.value.has(toNumberOrNull(option && option.value)))
    )

    function reduceOptionToValue(item) {
      if (item == null) return item
      return item.value != null ? item.value : item.id
    }

    function withPopperTop(dropdownList, component, config) {
      return popperHelper.withPopperTop(dropdownList, component, config)
    }

    function filterUsersByNameEmailAndUser(option, label, search) {
      const normalizedSearch = normalizeSearchText(search)
      if (!normalizedSearch) return true
      const normalizedLabel = normalizeSearchText(label)
      const normalizedOptionSearchText = normalizeSearchText(option && option.searchText)
      return normalizedLabel.includes(normalizedSearch) || normalizedOptionSearchText.includes(normalizedSearch)
    }

    async function loadTemplateFormCatalogForActivityType(nextActivityType) {
      if (!nextActivityType) {
        templateFormOptions.value = []
        templateFormsLoading.value = false
        return
      }
      templateFormsLoading.value = true
      try {
        const rows = await fetchTemplateFormRowsByActivityType(nextActivityType)
        templateFormOptions.value = mapPlanningTemplateRowsToSelectOptions(rows)
      } catch (_) {
        templateFormOptions.value = []
        toast({
          component: ToastificationContent,
          props: { title: 'Error loading form templates', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        templateFormsLoading.value = false
      }
    }

    async function syncTemplateFormsStateFromPlanning(planningItem) {
      await loadTemplateFormCatalogForActivityType(activityType.value)
      templateFormIds.value = normalizeTemplateFormIdsFromPlanningItem(planningItem)
    }

    function onStartDateChange(item) {
      if (!item || typeof item !== 'object') return
      const normalizedStartDate = normalizeDateInput(item.start_date)
      if (!normalizedStartDate) {
        item.end_date = ''
        return
      }
      const normalizedEndDate = normalizeDateInput(item.end_date)
      if (!normalizedEndDate) return
      if (!hasValidDateOrder(normalizedStartDate, normalizedEndDate)) item.end_date = ''
    }

    function onExtraUnitsInput(value) {
      selectedExtraUnitIds.value = Array.isArray(value) ? value : []
    }

    function toggleAddExtraUnits() {
      showAddExtraUnits.value = !showAddExtraUnits.value
      if (!showAddExtraUnits.value) selectedExtraUnitIds.value = []
    }

    function addReviewExtraItem(unit) {
      if (isPlanningUnitDisabled(unit)) return
      const extraItem = buildReviewExtraItemFromUnit({ unit, activityType: activityType.value })
      if (!extraItem) return
      const alreadyExists = editModeTableItems.value.some((row) => toNumberOrNull(row && row.unit_id) === toNumberOrNull(extraItem.unit_id))
      if (alreadyExists) return
      reviewExtraItems.value.push(extraItem)
      selectedUnitIds.value = [...new Set([...selectedUnitIds.value, extraItem.unit_id])]
    }

    function fetchUnitForExtraById(unitId) {
      if (activityType.value === 'risk') {
        return axios.get(`/v1/planning/get-units-risk/${unitId}`).then(({ data }) => {
          const unit = extractFirstUnitFromRiskResponse(data)
          if (!isAllowedUnitByMainUnit(unit)) return null
          return unit
        })
      }
      return axios.get(`/v1/unit/${unitId}`).then(({ data }) => {
        const unit = (data && data.data) || data
        if (!isAllowedUnitByMainUnit(unit)) return null
        return unit
      })
    }

    async function onAddExtraUnits() {
      const ids = Array.isArray(selectedExtraUnitIds.value) ? selectedExtraUnitIds.value : []
      for (let index = 0; index < ids.length; index += 1) {
        const unitId = ids[index]
        try {
          const unit = await fetchUnitForExtraById(unitId)
          if (!unit) continue
          addReviewExtraItem(unit)
        } catch (_) {}
      }
      selectedExtraUnitIds.value = []
      showAddExtraUnits.value = false
    }

    function goBack() {
      router.push({ name: 'planning' })
    }

    function toggleSelectAllCreate() {
      const newVal = !allSelectedCreate.value
      tableRows.value.forEach((row) => { row.selected = newVal })
    }
    function toggleSelectAllEdit() {
      const newVal = !allSelectedEdit.value
      editRowsForBulkSelectInEditMode.value.forEach((row) => { row.selected = newVal })
    }

    function isPlanningRowCheckboxDisabled(_row) {
      if (isPlanningHeaderOrTableShellDisabled.value) return true
      return false
    }

    function isPlanningRowCommentDisabled(_row) {
      if (isPlanningHeaderOrTableShellDisabled.value) return true
      return false
    }

    function isPlanningRowFieldsDisabled(row) {
      if (isPlanningHeaderOrTableShellDisabled.value) return true
      if (!row || row.selected !== true) return true
      return false
    }

    function addRowForUnit(unitId) {
      const uid = toNumberOrNull(unitId)
      if (uid == null) return
      tableRows.value.push({
        unit_id: uid,
        unit_name: '-',
        category_name: '-',
        last_sra_date: '-',
        residual_risk_level: '-',
        start_date: null,
        end_date: null,
        assigned_to_id: null,
        selected: true,
        origin: ORIGIN_UNIT,
        sector_id: null,
        region_id: null,
      })
      if (activityType.value === 'risk') {
        axios.get(`/v1/planning/get-units-risk/${uid}`).then(({ data }) => {
          const unit = extractFirstUnitFromRiskResponse(data)
          if (!unit) return
          if (!isAllowedUnitByMainUnit(unit)) {
            const removeIndex = tableRows.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
            if (removeIndex !== -1) tableRows.value.splice(removeIndex, 1)
            syncSelectedUnitIdsFromCreateTableRows()
            trimLastManualUnitsIfCreateTableEmpty()
            return
          }
          if (isPlanningUnitDisabled(unit)) {
            const removeIndex = tableRows.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
            if (removeIndex !== -1) tableRows.value.splice(removeIndex, 1)
            syncSelectedUnitIdsFromCreateTableRows()
            trimLastManualUnitsIfCreateTableEmpty()
            return
          }
          const row = tableRows.value.find((r) => toNumberOrNull(r.unit_id) === uid)
          if (!row) return
          row.unit_name = unit.description || unit.name || '-'
          row.category_name = getUnitCategoryName(unit)
          row.last_sra_date = getAcceptedAtDate(unit)
          row.residual_risk_level = getResidualRiskLevel(unit)
          const assignableUserId = getAssignableUserId({ unit, assignableUserIds: assignableUserIds.value })
          if (row.assigned_to_id == null && assignableUserId != null) row.assigned_to_id = assignableUserId
          const acceptedAt = unit.last_accepted_risk_evaluation && unit.last_accepted_risk_evaluation.accepted_at
          const normalizedLevel = normalizeRiskLevel(unit.threats_summary && unit.threats_summary.global && unit.threats_summary.global.level)
          const window = computeRiskWindow(acceptedAt, normalizedLevel, moment())
          if (row.start_date == null || String(row.start_date).trim() === '') row.start_date = window.start || null
          if (row.end_date == null || String(row.end_date).trim() === '') row.end_date = window.end || null
          if (!lastManualUnits.value.some((u) => u && (u.id === unit.id || u.value === unit.id))) lastManualUnits.value.push(unit)
        }).catch(() => {})
        return
      }
      axios.get(`/v1/unit/${uid}`).then(({ data }) => {
        const item = (data && data.data) || data
        if (!item || item.id == null) return
        if (!isAllowedUnitByMainUnit(item)) {
          const removeIndex = tableRows.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
          if (removeIndex !== -1) tableRows.value.splice(removeIndex, 1)
          syncSelectedUnitIdsFromCreateTableRows()
          trimLastManualUnitsIfCreateTableEmpty()
          return
        }
        if (isPlanningUnitDisabled(item)) {
          const removeIndex = tableRows.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
          if (removeIndex !== -1) tableRows.value.splice(removeIndex, 1)
          syncSelectedUnitIdsFromCreateTableRows()
          trimLastManualUnitsIfCreateTableEmpty()
          return
        }
        const row = tableRows.value.find((r) => toNumberOrNull(r.unit_id) === uid)
        if (row) {
          row.unit_name = item.description || '-'
          row.category_name = getUnitCategoryName(item)
        }
      }).catch(() => {})
    }

    function rowFromUnit(unit, assignableUserIds) {
      if (isPlanningUnitDisabled(unit)) return null
      const rawId = unit && (unit.id != null ? unit.id : unit.value)
      const id = toNumberOrNull(rawId)
      if (id == null) return null
      const acceptedAt = unit && unit.last_accepted_risk_evaluation && unit.last_accepted_risk_evaluation.accepted_at
      const normalizedLevel = normalizeRiskLevel(unit && unit.threats_summary && unit.threats_summary.global && unit.threats_summary.global.level)
      const riskWindow = computeRiskWindow(acceptedAt, normalizedLevel, moment())
      const assignableUserId = getAssignableUserId({ unit, assignableUserIds })
      return {
        unit_id: id,
        unit_name: (unit && (unit.description || unit.name)) || '-',
        category_name: getUnitCategoryName(unit),
        last_sra_date: getAcceptedAtDate(unit),
        residual_risk_level: getResidualRiskLevel(unit),
        risk_window_start: riskWindow.start,
        risk_window_end: riskWindow.end,
        start_date: null,
        end_date: null,
        assigned_to_id: assignableUserId,
        selected: true,
      }
    }

    function buildRowsByIdMap(units, assignableUserIds) {
      const map = new Map()
      const list = Array.isArray(units) ? units : []
      list.forEach((u) => {
        const row = rowFromUnit(u, assignableUserIds)
        if (!row) return
        const rowId = toNumberOrNull(row.unit_id)
        if (rowId != null) map.set(rowId, row)
      })
      return map
    }

    function applyBulkExcludedFromUnitsDeselect(previousIds, nextIds) {
      const bulkUnion = new Set([...lastSectorUnitIds.value, ...lastRegionUnitIds.value])
      const prevNorm = uniqueDefinedNumbers(previousIds || [])
      const nextNorm = uniqueDefinedNumbers(nextIds || [])
      const nextSet = new Set(nextNorm)
      const excluded = new Set(bulkExcludedUnitIds.value)
      prevNorm.forEach((id) => {
        if (nextSet.has(id)) return
        if (bulkUnion.has(id)) excluded.add(id)
      })
      nextNorm.forEach((id) => {
        excluded.delete(id)
      })
      bulkExcludedUnitIds.value = excluded
    }

    function maybeClearBulkExclusionsWhenNoBulk() {
      if (lastSectorUnitIds.value.size === 0 && lastRegionUnitIds.value.size === 0) {
        bulkExcludedUnitIds.value = new Set()
      }
    }

    function syncSelectedUnitIdsFromCreateTableRows() {
      selectedUnitIds.value = uniqueDefinedNumbers(tableRows.value.map((r) => r && r.unit_id))
    }

    function trimLastManualUnitsIfCreateTableEmpty() {
      if (!isCreateMode.value) return
      if (tableRows.value.length > 0) return
      lastManualUnits.value = []
    }

    function applyCreateSectorUnitsFromFetch(units, sectorSourceId, sectorRowsMap) {
      const sid = toNumberOrNull(sectorSourceId)
      if (sid == null) return
      const map = sectorRowsMap instanceof Map ? sectorRowsMap : new Map()
      const list = Array.isArray(units) ? units : []
      list.forEach((rawUnit) => {
        const uid = toNumberOrNull(rawUnit && (rawUnit.id != null ? rawUnit.id : rawUnit.value))
        if (uid == null) return
        const template = map.get(uid)
        const rowIndex = tableRows.value.findIndex((r) => toNumberOrNull(r && r.unit_id) === uid)
        const existing = rowIndex !== -1 ? tableRows.value[rowIndex] : null
        if (!existing) {
          const templateRow = template || {}
          const base = {
            unit_id: uid,
            unit_name: templateRow.unit_name || '-',
            category_name: templateRow.category_name || '-',
            last_sra_date: templateRow.last_sra_date || '-',
            residual_risk_level: templateRow.residual_risk_level || '-',
            risk_window_start: templateRow.risk_window_start || '',
            risk_window_end: templateRow.risk_window_end || '',
            start_date: null,
            end_date: null,
            assigned_to_id: templateRow.assigned_to_id != null ? templateRow.assigned_to_id : null,
            selected: true,
            origin: ORIGIN_SECTOR,
            sector_id: sid,
            region_id: null,
          }
          tableRows.value.push(applyRiskWindowToCreateRow(base, activityType.value))
          return
        }
        const existingSector = toNumberOrNull(existing.sector_id)
        const existingRegion = toNumberOrNull(existing.region_id)
        const isManual = existingSector == null && existingRegion == null
        if (isManual) {
          const merged = {
            ...existing,
            origin: ORIGIN_SECTOR,
            sector_id: sid,
            region_id: null,
          }
          if (template) {
            if (template.unit_name != null) merged.unit_name = template.unit_name
            if (template.category_name != null) merged.category_name = template.category_name
            if (template.last_sra_date != null) merged.last_sra_date = template.last_sra_date
            if (template.residual_risk_level != null) merged.residual_risk_level = template.residual_risk_level
            if (template.risk_window_start != null) merged.risk_window_start = template.risk_window_start
            if (template.risk_window_end != null) merged.risk_window_end = template.risk_window_end
            if (merged.assigned_to_id == null && template.assigned_to_id != null) merged.assigned_to_id = template.assigned_to_id
          }
          tableRows.value[rowIndex] = applyRiskWindowToCreateRow(merged, activityType.value)
          return
        }
        const merged = {
          ...existing,
          sector_id: sid,
          origin: resolveCreateOriginFromProvenance(sid, existing.region_id),
        }
        if (template) {
          if (template.unit_name != null) merged.unit_name = template.unit_name
          if (template.category_name != null) merged.category_name = template.category_name
          if (template.last_sra_date != null) merged.last_sra_date = template.last_sra_date
          if (template.residual_risk_level != null) merged.residual_risk_level = template.residual_risk_level
          if (template.risk_window_start != null) merged.risk_window_start = template.risk_window_start
          if (template.risk_window_end != null) merged.risk_window_end = template.risk_window_end
          if (merged.assigned_to_id == null && template.assigned_to_id != null) merged.assigned_to_id = template.assigned_to_id
        }
        tableRows.value[rowIndex] = applyRiskWindowToCreateRow(merged, activityType.value)
      })
    }

    function applyCreateRegionUnitsFromFetch(units, regionSourceId, regionRowsMap) {
      const rid = toNumberOrNull(regionSourceId)
      if (rid == null) return
      const map = regionRowsMap instanceof Map ? regionRowsMap : new Map()
      const list = Array.isArray(units) ? units : []
      list.forEach((rawUnit) => {
        const uid = toNumberOrNull(rawUnit && (rawUnit.id != null ? rawUnit.id : rawUnit.value))
        if (uid == null) return
        const template = map.get(uid)
        const rowIndex = tableRows.value.findIndex((r) => toNumberOrNull(r && r.unit_id) === uid)
        const existing = rowIndex !== -1 ? tableRows.value[rowIndex] : null
        if (!existing) {
          const templateRow = template || {}
          const base = {
            unit_id: uid,
            unit_name: templateRow.unit_name || '-',
            category_name: templateRow.category_name || '-',
            last_sra_date: templateRow.last_sra_date || '-',
            residual_risk_level: templateRow.residual_risk_level || '-',
            risk_window_start: templateRow.risk_window_start || '',
            risk_window_end: templateRow.risk_window_end || '',
            start_date: null,
            end_date: null,
            assigned_to_id: templateRow.assigned_to_id != null ? templateRow.assigned_to_id : null,
            selected: true,
            origin: ORIGIN_REGION,
            sector_id: null,
            region_id: rid,
          }
          tableRows.value.push(applyRiskWindowToCreateRow(base, activityType.value))
          return
        }
        const existingSector = toNumberOrNull(existing.sector_id)
        const existingRegion = toNumberOrNull(existing.region_id)
        const isManual = existingSector == null && existingRegion == null
        if (isManual) {
          const merged = {
            ...existing,
            origin: ORIGIN_REGION,
            sector_id: null,
            region_id: rid,
          }
          if (template) {
            if (template.unit_name != null) merged.unit_name = template.unit_name
            if (template.category_name != null) merged.category_name = template.category_name
            if (template.last_sra_date != null) merged.last_sra_date = template.last_sra_date
            if (template.residual_risk_level != null) merged.residual_risk_level = template.residual_risk_level
            if (template.risk_window_start != null) merged.risk_window_start = template.risk_window_start
            if (template.risk_window_end != null) merged.risk_window_end = template.risk_window_end
            if (merged.assigned_to_id == null && template.assigned_to_id != null) merged.assigned_to_id = template.assigned_to_id
          }
          tableRows.value[rowIndex] = applyRiskWindowToCreateRow(merged, activityType.value)
          return
        }
        const merged = {
          ...existing,
          region_id: rid,
          origin: resolveCreateOriginFromProvenance(existing.sector_id, rid),
        }
        if (template) {
          if (template.unit_name != null) merged.unit_name = template.unit_name
          if (template.category_name != null) merged.category_name = template.category_name
          if (template.last_sra_date != null) merged.last_sra_date = template.last_sra_date
          if (template.residual_risk_level != null) merged.residual_risk_level = template.residual_risk_level
          if (template.risk_window_start != null) merged.risk_window_start = template.risk_window_start
          if (template.risk_window_end != null) merged.risk_window_end = template.risk_window_end
          if (merged.assigned_to_id == null && template.assigned_to_id != null) merged.assigned_to_id = template.assigned_to_id
        }
        tableRows.value[rowIndex] = applyRiskWindowToCreateRow(merged, activityType.value)
      })
    }

    function clearCreateSectorProvenanceFromTableRows() {
      tableRows.value = tableRows.value.reduce((accumulator, row) => {
        const sectorId = toNumberOrNull(row && row.sector_id)
        const regionId = toNumberOrNull(row && row.region_id)
        if (sectorId == null) {
          accumulator.push(row)
          return accumulator
        }
        if (regionId == null) return accumulator
        const merged = {
          ...row,
          sector_id: null,
          origin: resolveCreateOriginFromProvenance(null, regionId),
        }
        accumulator.push(applyRiskWindowToCreateRow(merged, activityType.value))
        return accumulator
      }, [])
    }

    function clearCreateRegionProvenanceFromTableRows() {
      tableRows.value = tableRows.value.reduce((accumulator, row) => {
        const sectorId = toNumberOrNull(row && row.sector_id)
        const regionId = toNumberOrNull(row && row.region_id)
        if (regionId == null) {
          accumulator.push(row)
          return accumulator
        }
        if (sectorId == null) return accumulator
        const merged = {
          ...row,
          region_id: null,
          origin: resolveCreateOriginFromProvenance(sectorId, null),
        }
        accumulator.push(applyRiskWindowToCreateRow(merged, activityType.value))
        return accumulator
      }, [])
    }

    function editableItemFromRow(unitId, row, existingEditable, origin, sectorId, regionId) {
      const base = row
        ? {
            unit_name: row.unit_name || '-',
            category_name: row.category_name || '-',
            last_sra_date: row.last_sra_date || '-',
            residual_risk_level: row.residual_risk_level || '-',
            risk_window_start: row.risk_window_start || '',
            risk_window_end: row.risk_window_end || '',
          }
        : { unit_name: '-', category_name: '-', last_sra_date: '-', residual_risk_level: '-', risk_window_start: '', risk_window_end: '' }
      const prev = existingEditable || null
      const prevUnitName = prev && prev.unit_name != null ? String(prev.unit_name).trim() : ''
      const prevCategoryName = prev && prev.category_name != null ? String(prev.category_name).trim() : ''
      const baseUnitName = base.unit_name != null ? String(base.unit_name).trim() : ''
      const baseCategoryName = base.category_name != null ? String(base.category_name).trim() : ''
      const unit_name = (baseUnitName && baseUnitName !== '-') ? baseUnitName : ((prevUnitName && prevUnitName !== '-') ? prevUnitName : '-')
      const category_name = (baseCategoryName && baseCategoryName !== '-') ? baseCategoryName : ((prevCategoryName && prevCategoryName !== '-') ? prevCategoryName : '-')
      return {
        id: prev && prev.id != null ? prev.id : null,
        unit_id: unitId,
        unit_name,
        category_name,
        last_sra_date: (prev && prev.last_sra_date != null ? prev.last_sra_date : base.last_sra_date) || '-',
        residual_risk_level: (prev && prev.residual_risk_level != null ? prev.residual_risk_level : base.residual_risk_level) || '-',
        risk_window_start: (prev && prev.risk_window_start != null ? prev.risk_window_start : base.risk_window_start) || '',
        risk_window_end: (prev && prev.risk_window_end != null ? prev.risk_window_end : base.risk_window_end) || '',
        start_date: prev && prev.start_date != null ? String(prev.start_date) : '',
        end_date: prev && prev.end_date != null ? String(prev.end_date) : '',
        user_id: prev && prev.user_id != null ? prev.user_id : null,
        comment: prev && prev.comment != null ? String(prev.comment) : '',
        pending_deletion_marked: !!(prev && prev.pending_deletion_marked)
          || commentEndsWithDeletionMarker(prev && prev.comment != null ? String(prev.comment) : ''),
        selected: prev != null ? prev.selected === true : true,
        origin: origin ?? (prev && prev.origin != null ? prev.origin : null),
        sector_id: sectorId ?? (prev && prev.sector_id != null ? prev.sector_id : null),
        region_id: regionId ?? (prev && prev.region_id != null ? prev.region_id : null),
      }
    }

    function applyMergedToEditMode(mergedIds, sectorRows, regionRows, existingEditablesById, sectorIdSet, regionIdSet, sourceSectorIds, sourceRegionIds) {
      const sectorSet = sectorIdSet instanceof Set ? sectorIdSet : new Set()
      const regionSet = regionIdSet instanceof Set ? regionIdSet : new Set()
      const sectorIdsArray = Array.isArray(sourceSectorIds) ? sourceSectorIds : []
      const regionIdsArray = Array.isArray(sourceRegionIds) ? sourceRegionIds : []
      editableItems.value = mergedIds.map((unitId) => {
        const origin = getOriginForUnitId(unitId, sectorSet, regionSet)
        const sector_id = origin === ORIGIN_SECTOR ? (sectorIdsArray[0] ?? null) : null
        const region_id = origin === ORIGIN_REGION ? (regionIdsArray[0] ?? null) : null
        const sectorRow = sectorRows.get(unitId)
        const regionRow = regionRows.get(unitId)
        const row = sectorRow || regionRow
        const existing = existingEditablesById.get(unitId)
        const base = editableItemFromRow(unitId, row, existing, origin, sector_id, region_id)
        return applyRiskWindowToEditableItem(base, activityType.value)
      })
      selectedUnitIds.value = mergedIds
    }

    function parseUnitsListFromPlanningBulkResponse(responseData) {
      const raw = (responseData && responseData.data) || responseData
      const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.data) ? raw.data : [])
      const filteredByMainUnit = filterUnitsByMainUnit(list)
      const withValidId = filteredByMainUnit.filter((u) => u && (u.id != null || u.value != null))
      return filterActivePlanningUnits(withValidId)
    }

    async function fetchUnitsBySectorAndFillTable(ids) {
      if (!isCreateMode.value && isDivisionRegionUnitsSelectionLocked.value) return
      if (!isCreateMode.value && isSuppressingSectorRegionBulkSync.value) return
      const idsParam = Array.isArray(ids) ? ids : [ids]
      const param = idsParam.filter((id) => id != null && id !== '')
      if (param.length === 0) {
        if (isCreateMode.value) {
          clearCreateSectorProvenanceFromTableRows()
          lastSectorUnitIds.value = new Set()
          lastSectorUnits.value = []
          syncSelectedUnitIdsFromCreateTableRows()
          trimLastManualUnitsIfCreateTableEmpty()
          return
        }
        const manualIds = getManualUnitIdsFromSets(selectedUnitIds.value, lastSectorUnitIds.value, lastRegionUnitIds.value)
        lastSectorUnitIds.value = new Set()
        lastSectorUnits.value = []
        const mergedIds = computeMergedUnitIds({
          sectorIdsSet: lastSectorUnitIds.value,
          regionIdsSet: lastRegionUnitIds.value,
          manualIds,
          excludedSet: bulkExcludedUnitIds.value,
        })
        maybeClearBulkExclusionsWhenNoBulk()
        const regionRows = buildRowsByIdMap(lastRegionUnits.value, assignableUserIds.value)
        const sectorIdSet = new Set()
        const regionIdSet = lastRegionUnitIds.value
        const existingEditablesById = new Map()
        editableItems.value.forEach((r) => {
          const rowId = toNumberOrNull(r && r.unit_id)
          if (rowId != null) existingEditablesById.set(rowId, r)
        })
        applyMergedToEditMode(mergedIds, new Map(), regionRows, existingEditablesById, sectorIdSet, regionIdSet, sectorIds.value || [], regionIds.value || [])
        return
      }
      try {
        if (isCreateMode.value) {
          tableRows.value = tableRows.value.filter((row) => {
            const sectorNumeric = toNumberOrNull(row && row.sector_id)
            const regionNumeric = toNumberOrNull(row && row.region_id)
            if (sectorNumeric != null && regionNumeric == null) return false
            return true
          })
          const unionSectorUnitIds = new Set()
          const mergedSectorUnits = []
          for (let index = 0; index < param.length; index += 1) {
            const sid = toNumberOrNull(param[index])
            if (sid == null) continue
            const sectorParams = { sector_ids: String(sid) }
            if (activityType.value === 'risk') sectorParams.withRisk = true
            if (shouldFilterMainUnits.value) sectorParams.is_main_unit = 1
            const response = await axios.get('/v1/planning/get-units-by-sector', {
              params: sectorParams,
            })
            const units = await filterUnitsByMainUnitWithFallback(
              parseUnitsListFromPlanningBulkResponse(response.data)
            )
            units.forEach((unit) => mergedSectorUnits.push(unit))
            units.forEach((u) => {
              const uid = toNumberOrNull(u.id != null ? u.id : u.value)
              if (uid != null) unionSectorUnitIds.add(uid)
            })
            const sectorRows = buildRowsByIdMap(units, assignableUserIds.value)
            applyCreateSectorUnitsFromFetch(units, sid, sectorRows)
          }
          lastSectorUnitIds.value = unionSectorUnitIds
          lastSectorUnits.value = mergedSectorUnits
          syncSelectedUnitIdsFromCreateTableRows()
          trimLastManualUnitsIfCreateTableEmpty()
        } else {
          const sectorParams = { sector_ids: param.join(',') }
          if (activityType.value === 'risk') sectorParams.withRisk = true
          if (shouldFilterMainUnits.value) sectorParams.is_main_unit = 1
          const response = await axios.get('/v1/planning/get-units-by-sector', {
            params: sectorParams,
          })
          const units = await filterUnitsByMainUnitWithFallback(
            parseUnitsListFromPlanningBulkResponse(response.data)
          )
          const sectorIdSet = buildNormalizedUnitIdSet(units.map((u) => (u.id != null ? u.id : u.value)))
          lastSectorUnitIds.value = sectorIdSet
          lastSectorUnits.value = units
          const regionIdSet = lastRegionUnitIds.value
          const sectorRows = buildRowsByIdMap(units, assignableUserIds.value)
          const manualIds = getManualUnitIdsFromSets(selectedUnitIds.value, lastSectorUnitIds.value, lastRegionUnitIds.value)
          const mergedIds = computeMergedUnitIds({
            sectorIdsSet: sectorIdSet,
            regionIdsSet: regionIdSet,
            manualIds,
            excludedSet: bulkExcludedUnitIds.value,
          })
          maybeClearBulkExclusionsWhenNoBulk()
          const regionRows = buildRowsByIdMap(lastRegionUnits.value, assignableUserIds.value)
          const existingEditablesById = new Map()
          editableItems.value.forEach((r) => {
            const rowId = toNumberOrNull(r && r.unit_id)
            if (rowId != null) existingEditablesById.set(rowId, r)
          })
          applyMergedToEditMode(mergedIds, sectorRows, regionRows, existingEditablesById, sectorIdSet, regionIdSet, sectorIds.value || [], regionIds.value || [])
        }
      } catch (_) {
        if (isCreateMode.value) {
          clearCreateSectorProvenanceFromTableRows()
          lastSectorUnitIds.value = new Set()
          lastSectorUnits.value = []
          syncSelectedUnitIdsFromCreateTableRows()
          trimLastManualUnitsIfCreateTableEmpty()
        } else {
          const manualIds = getManualUnitIdsFromSets(selectedUnitIds.value, lastSectorUnitIds.value, lastRegionUnitIds.value)
          lastSectorUnitIds.value = new Set()
          lastSectorUnits.value = []
          const mergedIds = computeMergedUnitIds({
            sectorIdsSet: lastSectorUnitIds.value,
            regionIdsSet: lastRegionUnitIds.value,
            manualIds,
            excludedSet: bulkExcludedUnitIds.value,
          })
          maybeClearBulkExclusionsWhenNoBulk()
          const regionRows = buildRowsByIdMap(lastRegionUnits.value, assignableUserIds.value)
          const sectorIdSet = new Set()
          const regionIdSet = lastRegionUnitIds.value
          const existingEditablesById = new Map()
          editableItems.value.forEach((r) => {
            const rowId = toNumberOrNull(r && r.unit_id)
            if (rowId != null) existingEditablesById.set(rowId, r)
          })
          applyMergedToEditMode(mergedIds, new Map(), regionRows, existingEditablesById, sectorIdSet, regionIdSet, sectorIds.value || [], regionIds.value || [])
        }
      }
    }

    async function fetchUnitsByRegionAndFillTable(ids) {
      if (!isCreateMode.value && isDivisionRegionUnitsSelectionLocked.value) return
      if (!isCreateMode.value && isSuppressingSectorRegionBulkSync.value) return
      const idsParam = Array.isArray(ids) ? ids : [ids]
      const param = idsParam.filter((id) => id != null && id !== '')
      if (param.length === 0) {
        if (isCreateMode.value) {
          clearCreateRegionProvenanceFromTableRows()
          lastRegionUnitIds.value = new Set()
          lastRegionUnits.value = []
          syncSelectedUnitIdsFromCreateTableRows()
          trimLastManualUnitsIfCreateTableEmpty()
          return
        }
        const manualIds = getManualUnitIdsFromSets(selectedUnitIds.value, lastSectorUnitIds.value, lastRegionUnitIds.value)
        lastRegionUnitIds.value = new Set()
        lastRegionUnits.value = []
        const mergedIds = computeMergedUnitIds({
          sectorIdsSet: lastSectorUnitIds.value,
          regionIdsSet: lastRegionUnitIds.value,
          manualIds,
          excludedSet: bulkExcludedUnitIds.value,
        })
        maybeClearBulkExclusionsWhenNoBulk()
        const sectorRows = buildRowsByIdMap(lastSectorUnits.value, assignableUserIds.value)
        const sectorIdSet = lastSectorUnitIds.value
        const regionIdSet = new Set()
        const existingEditablesById = new Map()
        editableItems.value.forEach((r) => {
          const rowId = toNumberOrNull(r && r.unit_id)
          if (rowId != null) existingEditablesById.set(rowId, r)
        })
        applyMergedToEditMode(mergedIds, sectorRows, new Map(), existingEditablesById, sectorIdSet, regionIdSet, sectorIds.value || [], regionIds.value || [])
        return
      }
      try {
        if (isCreateMode.value) {
          tableRows.value = tableRows.value.filter((row) => {
            const sectorNumeric = toNumberOrNull(row && row.sector_id)
            const regionNumeric = toNumberOrNull(row && row.region_id)
            if (regionNumeric != null && sectorNumeric == null) return false
            return true
          })
          const unionRegionUnitIds = new Set()
          const mergedRegionUnits = []
          for (let index = 0; index < param.length; index += 1) {
            const rid = toNumberOrNull(param[index])
            if (rid == null) continue
            const regionParams = { region_ids: String(rid) }
            if (activityType.value === 'risk') regionParams.withRisk = true
            if (shouldFilterMainUnits.value) regionParams.is_main_unit = 1
            const response = await axios.get('/v1/planning/get-units-by-region', {
              params: regionParams,
            })
            const units = await filterUnitsByMainUnitWithFallback(
              parseUnitsListFromPlanningBulkResponse(response.data)
            )
            units.forEach((unit) => mergedRegionUnits.push(unit))
            units.forEach((u) => {
              const uid = toNumberOrNull(u.id != null ? u.id : u.value)
              if (uid != null) unionRegionUnitIds.add(uid)
            })
            const regionRows = buildRowsByIdMap(units, assignableUserIds.value)
            applyCreateRegionUnitsFromFetch(units, rid, regionRows)
          }
          lastRegionUnitIds.value = unionRegionUnitIds
          lastRegionUnits.value = mergedRegionUnits
          syncSelectedUnitIdsFromCreateTableRows()
          trimLastManualUnitsIfCreateTableEmpty()
        } else {
          const regionParams = { region_ids: param.join(',') }
          if (activityType.value === 'risk') regionParams.withRisk = true
          if (shouldFilterMainUnits.value) regionParams.is_main_unit = 1
          const response = await axios.get('/v1/planning/get-units-by-region', {
            params: regionParams,
          })
          const units = await filterUnitsByMainUnitWithFallback(
            parseUnitsListFromPlanningBulkResponse(response.data)
          )
          const regionIdSet = buildNormalizedUnitIdSet(units.map((u) => (u.id != null ? u.id : u.value)))
          lastRegionUnitIds.value = regionIdSet
          lastRegionUnits.value = units
          const sectorIdSet = lastSectorUnitIds.value
          const sectorRows = buildRowsByIdMap(lastSectorUnits.value, assignableUserIds.value)
          const regionRows = buildRowsByIdMap(units, assignableUserIds.value)
          const manualIds = getManualUnitIdsFromSets(selectedUnitIds.value, lastSectorUnitIds.value, lastRegionUnitIds.value)
          const mergedIds = computeMergedUnitIds({
            sectorIdsSet: sectorIdSet,
            regionIdsSet: regionIdSet,
            manualIds,
            excludedSet: bulkExcludedUnitIds.value,
          })
          maybeClearBulkExclusionsWhenNoBulk()
          const existingEditablesById = new Map()
          editableItems.value.forEach((r) => {
            const rowId = toNumberOrNull(r && r.unit_id)
            if (rowId != null) existingEditablesById.set(rowId, r)
          })
          applyMergedToEditMode(mergedIds, sectorRows, regionRows, existingEditablesById, sectorIdSet, regionIdSet, sectorIds.value || [], regionIds.value || [])
        }
      } catch (_) {
        if (isCreateMode.value) {
          clearCreateRegionProvenanceFromTableRows()
          lastRegionUnitIds.value = new Set()
          lastRegionUnits.value = []
          syncSelectedUnitIdsFromCreateTableRows()
          trimLastManualUnitsIfCreateTableEmpty()
        } else {
          const manualIds = getManualUnitIdsFromSets(selectedUnitIds.value, lastSectorUnitIds.value, lastRegionUnitIds.value)
          lastRegionUnitIds.value = new Set()
          lastRegionUnits.value = []
          const mergedIds = computeMergedUnitIds({
            sectorIdsSet: lastSectorUnitIds.value,
            regionIdsSet: lastRegionUnitIds.value,
            manualIds,
            excludedSet: bulkExcludedUnitIds.value,
          })
          maybeClearBulkExclusionsWhenNoBulk()
          const sectorRows = buildRowsByIdMap(lastSectorUnits.value, assignableUserIds.value)
          const sectorIdSet = lastSectorUnitIds.value
          const regionIdSet = new Set()
          const existingEditablesById = new Map()
          editableItems.value.forEach((r) => {
            const rowId = toNumberOrNull(r && r.unit_id)
            if (rowId != null) existingEditablesById.set(rowId, r)
          })
          applyMergedToEditMode(mergedIds, sectorRows, new Map(), existingEditablesById, sectorIdSet, regionIdSet, sectorIds.value || [], regionIds.value || [])
        }
      }
    }

    function addEditableItemForUnit(unitId) {
      const uid = toNumberOrNull(unitId)
      if (uid == null) return
      editableItems.value.push({
        id: null,
        unit_id: uid,
        unit_name: '-',
        category_name: '-',
        last_sra_date: '-',
        residual_risk_level: '-',
        start_date: '',
        end_date: '',
        user_id: null,
        comment: '',
        pending_deletion_marked: false,
        selected: true,
        origin: ORIGIN_UNIT,
        sector_id: null,
        region_id: null,
      })
      if (activityType.value === 'risk') {
        axios.get(`/v1/planning/get-units-risk/${uid}`).then(({ data }) => {
          const unit = extractFirstUnitFromRiskResponse(data)
          if (!unit) return
          if (!isAllowedUnitByMainUnit(unit)) {
            const removeIndex = editableItems.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
            if (removeIndex !== -1) editableItems.value.splice(removeIndex, 1)
            selectedUnitIds.value = selectedUnitIds.value.filter((id) => toNumberOrNull(id) !== uid)
            return
          }
          if (isPlanningUnitDisabled(unit)) {
            const removeIndex = editableItems.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
            if (removeIndex !== -1) editableItems.value.splice(removeIndex, 1)
            selectedUnitIds.value = selectedUnitIds.value.filter((id) => toNumberOrNull(id) !== uid)
            return
          }
          const row = editableItems.value.find((r) => toNumberOrNull(r.unit_id) === uid)
          if (!row) return
          row.unit_name = unit.description || unit.name || '-'
          row.category_name = getUnitCategoryName(unit)
          row.last_sra_date = getAcceptedAtDate(unit)
          row.residual_risk_level = getResidualRiskLevel(unit)
          const assignableUserId = getAssignableUserId({ unit, assignableUserIds: assignableUserIds.value })
          if (row.user_id == null && assignableUserId != null) row.user_id = assignableUserId
          const acceptedAt = unit.last_accepted_risk_evaluation && unit.last_accepted_risk_evaluation.accepted_at
          const normalizedLevel = normalizeRiskLevel(unit.threats_summary && unit.threats_summary.global && unit.threats_summary.global.level)
          const window = computeRiskWindow(acceptedAt, normalizedLevel, moment())
          if (!row.start_date || String(row.start_date).trim() === '') row.start_date = window.start || ''
          if (!row.end_date || String(row.end_date).trim() === '') row.end_date = window.end || ''
          if (!lastManualUnits.value.some((u) => u && (u.id === unit.id || u.value === unit.id))) lastManualUnits.value.push(unit)
        }).catch(() => {})
        return
      }
      axios.get(`/v1/unit/${uid}`).then(({ data }) => {
        const item = (data && data.data) || data
        if (!item || item.id == null) return
        if (!isAllowedUnitByMainUnit(item)) {
          const removeIndex = editableItems.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
          if (removeIndex !== -1) editableItems.value.splice(removeIndex, 1)
          selectedUnitIds.value = selectedUnitIds.value.filter((id) => toNumberOrNull(id) !== uid)
          return
        }
        if (isPlanningUnitDisabled(item)) {
          const removeIndex = editableItems.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
          if (removeIndex !== -1) editableItems.value.splice(removeIndex, 1)
          selectedUnitIds.value = selectedUnitIds.value.filter((id) => toNumberOrNull(id) !== uid)
          return
        }
        const row = editableItems.value.find((r) => toNumberOrNull(r.unit_id) === uid)
        if (row) {
          row.unit_name = item.description || '-'
          row.category_name = getUnitCategoryName(item)
        }
      }).catch(() => {})
    }

    function onUnitsInput(val) {
      const ids = Array.isArray(val) ? val : (val != null ? [val] : [])
      const normalizedIds = uniqueDefinedNumbers(ids)
      if (!isCreateMode.value) applyBulkExcludedFromUnitsDeselect(selectedUnitIds.value, normalizedIds)
      selectedUnitIds.value = normalizedIds
      if (isCreateMode.value) {
        const existingIds = new Set(tableRows.value.map((r) => toNumberOrNull(r.unit_id)).filter((id) => id != null))
        const idSet = new Set(normalizedIds)
        const toAdd = normalizedIds.filter((id) => !existingIds.has(id))
        toAdd.forEach((unitId) => addRowForUnit(unitId))
        const toRemove = tableRows.value
          .filter((r) => {
            const uid = toNumberOrNull(r.unit_id)
            return uid == null || !idSet.has(uid)
          })
          .map((r) => toNumberOrNull(r.unit_id))
          .filter((id) => id != null)
        toRemove.forEach((unitId) => {
          const index = tableRows.value.findIndex((r) => toNumberOrNull(r.unit_id) === unitId)
          if (index !== -1) tableRows.value.splice(index, 1)
        })
        syncSelectedUnitIdsFromCreateTableRows()
        if (normalizedIds.length === 0) lastManualUnits.value = []
        trimLastManualUnitsIfCreateTableEmpty()
        return
      }
      const existingIds = new Set(editableItems.value.map((r) => toNumberOrNull(r.unit_id)).filter((id) => id != null))
      const idSetEdit = new Set(normalizedIds)
      const toAddEdit = normalizedIds.filter((id) => !existingIds.has(id))
      toAddEdit.forEach((unitId) => addEditableItemForUnit(unitId))
      const toRemoveEdit = editableItems.value
        .filter((r) => {
          const uid = toNumberOrNull(r.unit_id)
          return uid == null || !idSetEdit.has(uid)
        })
        .map((r) => toNumberOrNull(r.unit_id))
        .filter((id) => id != null)
      toRemoveEdit.forEach((unitId) => {
        const index = editableItems.value.findIndex((r) => toNumberOrNull(r.unit_id) === unitId)
        if (index !== -1) editableItems.value.splice(index, 1)
      })
    }

    function applyLocalRemoveEditRow(row) {
      if (!row) return
      const uid = toNumberOrNull(row.unit_id)
      if (!row.is_extra && uid != null) {
        const bulkUnion = new Set([...lastSectorUnitIds.value, ...lastRegionUnitIds.value])
        if (bulkUnion.has(uid)) {
          const excluded = new Set(bulkExcludedUnitIds.value)
          excluded.add(uid)
          bulkExcludedUnitIds.value = excluded
        }
      }
      if (row.is_extra) {
        const extraIndex = reviewExtraItems.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
        if (extraIndex !== -1) reviewExtraItems.value.splice(extraIndex, 1)
      } else {
        const editableIndex = editableItems.value.findIndex((r) => toNumberOrNull(r.unit_id) === uid)
        if (editableIndex !== -1) editableItems.value.splice(editableIndex, 1)
      }
      selectedUnitIds.value = selectedUnitIds.value.filter((id) => toNumberOrNull(id) !== uid)
    }

    function removeRow(index) {
      const row = tableRows.value[index]
      if (!row) return
      tableRows.value.splice(index, 1)
      syncSelectedUnitIdsFromCreateTableRows()
      trimLastManualUnitsIfCreateTableEmpty()
    }

    function removeEditTableRow(index) {
      const combined = editModeTableItems.value
      const row = combined[index]
      if (!row) return
      applyLocalRemoveEditRow(row)
    }

    function removeCreateRowsByPropertyTypes(typeLabels) {
      const removeSet = new Set(typeLabels)
      tableRows.value = tableRows.value.filter((row) => {
        const label = normalizePlanningPropertyCategoryLabel(row)
        if (label == null) return true
        return !removeSet.has(label)
      })
      syncSelectedUnitIdsFromCreateTableRows()
      trimLastManualUnitsIfCreateTableEmpty()
    }

    function removeEditRowsByPropertyTypes(typeLabels) {
      const removeSet = new Set(typeLabels)
      const matchesRemovedType = (row) => {
        const label = normalizePlanningPropertyCategoryLabel(row)
        return Boolean(label && removeSet.has(label))
      }
      const code = statusCode.value
      if (code === 'draft') {
        while (true) {
          const combined = editModeTableItems.value
          const index = combined.findIndex(matchesRemovedType)
          if (index === -1) break
          removeEditTableRow(index)
        }
        return
      }
      if (code === 'review' || code === 'additional') {
        editModeTableItems.value.forEach((row) => {
          if (!matchesRemovedType(row)) return
          markPlanningEditRowPendingDeletion(row)
        })
      }
    }

    function onCreatePropertyTypesFilterInput(newVal) {
      const next = Array.isArray(newVal) ? [...newVal] : []
      const prevSet = new Set(selectedPropertyTypeLabelsCreate.value)
      const nextSet = new Set(next)
      const removed = [...prevSet].filter((t) => !nextSet.has(t))
      if (removed.length > 0) removeCreateRowsByPropertyTypes(removed)
      const stillDistinct = new Set(collectDistinctPlanningPropertyTypes(tableRows.value))
      selectedPropertyTypeLabelsCreate.value = [...next]
        .filter((t) => stillDistinct.has(t))
        .sort((a, b) => a.localeCompare(b))
    }

    function onEditPropertyTypesFilterInput(newVal) {
      if (statusCode.value === 'draft' && isDraftAssigneeGateActive.value && !isCurrentUserPlanningRecordOwner.value) {
        return
      }
      const next = Array.isArray(newVal) ? [...newVal] : []
      const prevSet = new Set(selectedPropertyTypeLabelsEdit.value)
      const nextSet = new Set(next)
      const removed = [...prevSet].filter((t) => !nextSet.has(t))
      if (removed.length > 0) removeEditRowsByPropertyTypes(removed)
      const rows = editModeTableItems.value.filter((row) => !row.pending_deletion_marked)
      const stillDistinct = new Set(collectDistinctPlanningPropertyTypes(rows))
      selectedPropertyTypeLabelsEdit.value = [...next]
        .filter((t) => stillDistinct.has(t))
        .sort((a, b) => a.localeCompare(b))
    }

    function onTrashClick(index) {
      const combined = editModeTableItems.value
      const row = combined[index]
      if (!row) return
      if (statusCode.value === 'draft' && isDraftAssigneeGateActive.value) {
        if (!isCurrentUserPlanningRecordOwner.value) return
      }

      if (statusCode.value === 'draft') {
        removeEditTableRow(index)
        return
      }

      if (statusCode.value === 'review' || statusCode.value === 'additional') {
        markPlanningEditRowPendingDeletion(row)
        return
      }
    }

    function onPlanningCommentFieldInput(row, value) {
      if (!row) return
      const next = value != null ? String(value) : ''
      if (row.pending_deletion_marked || commentEndsWithDeletionMarker(row.comment)) {
        row.pending_deletion_marked = true
        row.comment = appendDeletionMarkerToComment(next)
        return
      }
      row.comment = next
    }

    async function onSubmitCreate() {
      const rowsForCreate = (tableRows.value || []).filter((r) => r && r.unit_id != null)
      if (!activityType.value || rowsForCreate.length === 0) return
      createSubmitting.value = true
      try {
        const payload = { status_id: 1, activity_type: activityType.value }
        const sids = sectorIds.value
        if (Array.isArray(sids) && sids.length > 0) payload.sector_ids = sids
        else if (sids != null && sids !== '') payload.sector_id = sids
        const rids = regionIds.value
        if (Array.isArray(rids) && rids.length > 0) payload.region_ids = rids
        else if (rids != null && rids !== '') payload.region_id = rids
        payload.templateForms = buildTemplateFormsPayload(templateFormIds.value)
        payload.items = rowsForCreate.map((r) => {
          const sectorId = r.sector_id ?? null
          const regionId = r.region_id ?? null
          const item = {
            unit_id: r.unit_id,
            origin: resolveCreateOriginFromProvenance(sectorId, regionId),
            sector_id: sectorId,
            region_id: regionId,
            is_active: r.selected === true,
          }
          const startStr = r.start_date && String(r.start_date).trim()
          if (startStr && startStr.length >= 10) item.start_date = startStr.slice(0, 10)
          const endStr = r.end_date && String(r.end_date).trim()
          if (endStr && endStr.length >= 10) item.target_date = endStr.slice(0, 10)
          item.user_id = toNumberOrNull(r.assigned_to_id)
          return item
        })
        const response = await store.dispatch('planning/add', payload)
        const id = extractCreatedPlanningId(response)
        if (id != null) router.push({ name: 'planningEdit', params: { id: String(id) } })
        else {
          toast({
            component: ToastificationContent,
            props: { title: 'Planning created but detail id was not found', icon: 'AlertTriangleIcon', variant: 'warning' },
          })
        }
      } catch (_) {
        toast({
          component: ToastificationContent,
          props: { title: 'Error creating planning', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        createSubmitting.value = false
      }
    }

    async function loadPlanning() {
      const id = planningId.value
      if (!id) return
      loading.value = true
      loadError.value = false
      editableItems.value = []
      reviewExtraItems.value = []
      selectedExtraUnitIds.value = []
      showAddExtraUnits.value = false
      bulkExcludedUnitIds.value = new Set()
      try {
        const raw = await store.dispatch('planning/get', {
          id,
          params: {
            getPlansAssigned: true,
            id_user: currentUserId.value,
          },
        })
        if (!raw) {
          loadError.value = true
          templateFormIds.value = []
          templateFormOptions.value = []
        } else {
          const item = getPlanningFromCurrent(raw)
          const rawActivity = typeof item.activity_type === 'string' ? item.activity_type : (item.activity_type && item.activity_type.name) || null
          activityType.value = normalizeActivityTypeFromApi(rawActivity)
          const items = Array.isArray(item.items) ? item.items : []
          const previousRowsByUnitId = new Map()
          editableItems.value.forEach((e) => {
            if (e.unit_id != null) previousRowsByUnitId.set(e.unit_id, e)
          })
          editableItems.value = buildEditableItems(item).map((row) =>
            applyLocalSelectionWhenServerOmitsIsActive(row, items, previousRowsByUnitId.get(row.unit_id))
          )
          selectedUnitIds.value = items.map((it) => {
            const u = resolveUnitFromItem(it)
            return it.unit_id ?? u?.id ?? null
          }).filter((id) => id != null)
          isSuppressingSectorRegionBulkSync.value = true
          try {
            const sector = item.sector
            const sectors = item.sectors
            if (Array.isArray(sectors) && sectors.length > 0) {
              sectorIds.value = sectors.map((s) => (s && s.id != null ? s.id : null)).filter((id) => id != null)
            } else if (sector && sector.id != null) {
              sectorIds.value = [sector.id]
            } else {
              sectorIds.value = extractSectorIdsFromPlanningItems(item)
            }
            const region = item.region
            const regions = item.regions
            if (Array.isArray(regions) && regions.length > 0) {
              regionIds.value = regions.map((r) => (r && r.id != null ? r.id : null)).filter((id) => id != null)
            } else if (region && region.id != null) {
              regionIds.value = [region.id]
            } else {
              regionIds.value = extractRegionIdsFromPlanningItems(item)
            }
            await syncTemplateFormsStateFromPlanning(item)
            await nextTick()
          } finally {
            isSuppressingSectorRegionBulkSync.value = false
          }
        }
      } catch (_) {
        loadError.value = true
      } finally {
        loading.value = false
      }
      if (!loadError.value) await syncWorkflowUserLists()
    }

    async function refetchCurrent() {
      const id = planningId.value
      if (!id) return
      bulkExcludedUnitIds.value = new Set()
      try {
        await store.dispatch('planning/get', {
          id,
          params: {
            getPlansAssigned: true,
            id_user: currentUserId.value,
          },
        })
        const raw = store.getters['planning/getCurrent']
        const item = getPlanningFromCurrent(raw)
        if (item) {
          const rawActivity = typeof item.activity_type === 'string' ? item.activity_type : (item.activity_type && item.activity_type.name) || null
          activityType.value = normalizeActivityTypeFromApi(rawActivity)
          const items = Array.isArray(item.items) ? item.items : []
          const previousRowsByUnitId = new Map()
          editableItems.value.forEach((e) => {
            if (e.unit_id != null) previousRowsByUnitId.set(e.unit_id, e)
          })
          editableItems.value = buildEditableItems(item).map((row) =>
            applyLocalSelectionWhenServerOmitsIsActive(row, items, previousRowsByUnitId.get(row.unit_id))
          )
          selectedUnitIds.value = items.map((it) => {
            const u = resolveUnitFromItem(it)
            return it.unit_id ?? u?.id ?? null
          }).filter((id) => id != null)
          isSuppressingSectorRegionBulkSync.value = true
          try {
            const sector = item.sector
            const sectors = item.sectors
            if (Array.isArray(sectors) && sectors.length > 0) {
              sectorIds.value = sectors.map((s) => (s && s.id != null ? s.id : null)).filter((id) => id != null)
            } else if (sector && sector.id != null) {
              sectorIds.value = [sector.id]
            } else {
              sectorIds.value = extractSectorIdsFromPlanningItems(item)
            }
            const region = item.region
            const regions = item.regions
            if (Array.isArray(regions) && regions.length > 0) {
              regionIds.value = regions.map((r) => (r && r.id != null ? r.id : null)).filter((id) => id != null)
            } else if (region && region.id != null) {
              regionIds.value = [region.id]
            } else {
              regionIds.value = extractRegionIdsFromPlanningItems(item)
            }
            await syncTemplateFormsStateFromPlanning(item)
            await nextTick()
          } finally {
            isSuppressingSectorRegionBulkSync.value = false
          }
        }
        reviewExtraItems.value = []
        selectedExtraUnitIds.value = []
        showAddExtraUnits.value = false
        await syncWorkflowUserLists()
      } catch (_) {}
    }

    function syncEditableItems() {
      if (!currentPlanning.value) return
      const rawItems = getPlanningItems(currentPlanning.value)
      const built = buildEditableItems(currentPlanning.value)
      const previousById = new Map()
      editableItems.value.forEach((e) => {
        if (e.unit_id != null) previousById.set(e.unit_id, e)
      })
      editableItems.value = built.map((row) => {
        const prev = row.unit_id != null ? previousById.get(row.unit_id) : null
        let merged
        if (row.unit_name !== '-' || row.unit_id == null) {
          merged = row
        } else if (!prev) {
          merged = row
        } else {
          merged = {
            ...row,
            unit_name: prev.unit_name !== '-' ? prev.unit_name : row.unit_name,
            category_name: prev.category_name !== '-' ? prev.category_name : row.category_name,
            last_sra_date: prev.last_sra_date !== '-' ? prev.last_sra_date : row.last_sra_date,
            residual_risk_level: prev.residual_risk_level !== '-' ? prev.residual_risk_level : row.residual_risk_level,
            comment: row.comment != null ? String(row.comment) : '',
            pending_deletion_marked: commentEndsWithDeletionMarker(row.comment) || !!prev.pending_deletion_marked,
          }
        }
        return applyLocalSelectionWhenServerOmitsIsActive(merged, rawItems, prev)
      })
      selectedUnitIds.value = editableItems.value.map((e) => e.unit_id).filter((id) => id != null)
    }

    function buildUpdateItemsPayload() {
      return {
        templateForms: buildTemplateFormsPayload(templateFormIds.value),
        items: editableItems.value.map((row) => mapEditablePlanningRowToItemPayload(row)).filter((row) => row != null),
      }
    }

    async function persistPlanningItems(id) {
      if (!id) return false
      if (statusCode.value === 'draft' && canUpdatePlanning.value && !isCurrentUserPlanningRecordOwner.value) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Only the planning owner can save changes in this state',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return false
      }
      const payload = buildUpdateItemsPayload()
      await store.dispatch('planning/updateItems', { id, payload })
      await refetchCurrent()
      syncEditableItems()
      return true
    }

    function buildReviewItemsPayload() {
      const existingItems = editableItems.value.map((row) => mapEditablePlanningRowToItemPayload(row)).filter((row) => row != null)
      const extraItems = reviewExtraItems.value.map((row) => mapReviewExtraItemToUpdatePayload(row)).filter((row) => row != null)
      return {
        templateForms: buildTemplateFormsPayload(templateFormIds.value),
        items: [...existingItems, ...extraItems],
      }
    }

    async function persistReviewItems(id) {
      if (!id || !isReviewFlowMode.value) return false
      const payload = buildReviewItemsPayload()
      await store.dispatch('planning/updateItems', { id, payload })
      await refetchCurrent()
      syncEditableItems()
      return true
    }

    async function onUnifiedSavePlanningItems() {
      const id = planningId.value
      if (!id) return
      itemsSaving.value = true
      try {
        if (isReviewFlowMode.value) await persistReviewItems(id)
        else await persistPlanningItems(id)
      } catch (_) {
        toast({
          component: ToastificationContent,
          props: { title: 'Error saving items', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        itemsSaving.value = false
      }
    }

    async function onSendToReview() {
      const id = planningId.value
      const userId = selectedReviewerId.value
      if (!id || !userId) return
      const pending = reviewPendingCounts.value
      if (pending.incompleteItemsCount > 0) {
        toast({
          component: ToastificationContent,
          props: buildIncompleteEditableItemsToastProps(pending),
        })
        return
      }
      workflowLoading.value = true
      try {
        const persisted = await persistPlanningItems(id)
        if (!persisted) return
        await store.dispatch('planning/updateStatus', { id, payload: { status: 'review', id_user: userId } })
        selectedReviewerId.value = null
        await refetchCurrent()
        router.push({ name: 'planning' })
      } catch (_) {
        toast({
          component: ToastificationContent,
          props: { title: 'Error sending to review', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        workflowLoading.value = false
      }
    }

    async function onSendToSecondApproval() {
      const id = planningId.value
      const userId = selectedSecondApproverId.value
      if (!id || !userId) return
      const pending = reviewPendingCounts.value
      if (pending.incompleteItemsCount > 0) {
        toast({
          component: ToastificationContent,
          props: buildIncompleteEditableItemsToastProps(pending),
        })
        return
      }
      workflowLoading.value = true
      try {
        await persistReviewItems(id)
        await store.dispatch('planning/updateStatus', { id, payload: { status: 'additional', id_user: userId } })
        selectedSecondApproverId.value = null
        await refetchCurrent()
        router.push({ name: 'planning' })
      } catch (_) {
        toast({
          component: ToastificationContent,
          props: { title: 'Error sending to second approval', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        workflowLoading.value = false
      }
    }

    async function onAcceptProgramming() {
      const id = planningId.value
      if (!id) return
      const pending = reviewPendingCounts.value
      if (pending.incompleteItemsCount > 0) {
        toast({
          component: ToastificationContent,
          props: buildIncompleteEditableItemsToastProps(pending),
        })
        return
      }
      workflowLoading.value = true
      try {
        await persistReviewItems(id)
        await store.dispatch('planning/updateStatus', { id, payload: { status: 'approved' } })
        await refetchCurrent()
        router.push({ name: 'planning' })
      } catch (_) {
        toast({
          component: ToastificationContent,
          props: { title: 'Error accepting programming', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        workflowLoading.value = false
      }
    }

    async function onRejectConfirm(evt) {
      const id = planningId.value
      const comment = rejectComment.value != null ? String(rejectComment.value).trim() : ''
      if (!id) return
      if (!comment) {
        if (evt) evt.preventDefault()
        toast({
          component: ToastificationContent,
          props: { title: 'Comment is required when rejecting', icon: 'AlertTriangleIcon', variant: 'warning' },
        })
        return
      }
      showRejectModal.value = false
      workflowLoading.value = true
      try {
        await persistReviewItems(id)
        const rejectStatus = statusCode.value === 'additional' ? 'review' : 'rejected'
        await store.dispatch('planning/updateStatus', { id, payload: { status: rejectStatus, comment } })
        rejectComment.value = ''
        await refetchCurrent()
      } catch (_) {
        toast({
          component: ToastificationContent,
          props: { title: 'Error rejecting', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        workflowLoading.value = false
      }
    }

    async function onSendProgramming() {
      const id = planningId.value
      if (!id) return
      const pending = getReviewPendingCounts(editModeTableItems.value)
      if (pending.incompleteItemsCount > 0) {
        toast({
          component: ToastificationContent,
          props: buildIncompleteEditableItemsToastProps(pending),
        })
        return
      }
      workflowLoading.value = true
      try {
        await persistPlanningItems(id)
        await store.dispatch('planning/updateStatus', { id, payload: { status: 'released' } })
        await refetchCurrent()
        router.push({ name: 'planning' })
      } catch (_) {
        toast({
          component: ToastificationContent,
          props: { title: 'Error sending programming', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        workflowLoading.value = false
      }
    }

    async function onDownloadPlanning() {
      const id = planningId.value
      if (!id) return
      workflowLoading.value = true
      try {
        const response = await axios.get(`/v1/planning/${id}/export`, { responseType: 'blob' })
        const contentType = response && response.headers && response.headers['content-type'] ? String(response.headers['content-type']) : ''
        if (contentType.includes('application/json')) throw new Error('Invalid export response')
        const filename = resolveDownloadFilenameFromHeaders(response && response.headers, `planning-${id}.xlsx`)
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (_) {
        toast({
          component: ToastificationContent,
          props: { title: 'Error downloading planning', icon: 'AlertTriangleIcon', variant: 'danger' },
        })
      } finally {
        workflowLoading.value = false
      }
    }

    async function loadPlanningUsers() {
      try {
        await store.dispatch('user/getAll', { max: 1000 })
      } catch (_) {}
    }

    async function bootstrapPlanningFormMount() {
      if (!canReadPlanning.value) {
        loadError.value = true
        return
      }
      store.dispatch('sector/getAll')
      store.dispatch('region/getAll')
      await loadPlanningUsers()
      if (!isCreateMode.value) await loadPlanning()
    }

    onMounted(() => {
      void bootstrapPlanningFormMount()
    })
    watch(planningId, async (id) => {
      if (!id) return
      if (!canReadPlanning.value) return
      await loadPlanningUsers()
      await loadPlanning()
    })
    watch(
      () => [currentItem.value, statusCode.value],
      () => syncEditableItems(),
      { deep: true }
    )
    watch(
      () => activityType.value,
      (newVal, oldVal) => {
        if (!isCreateMode.value) return
        if (oldVal === undefined && newVal == null) return
        if (newVal === oldVal) return
        templateFormIds.value = []
        templateFormOptions.value = []
        sectorIds.value = []
        regionIds.value = []
        selectedUnitIds.value = []
        tableRows.value = []
        lastSectorUnitIds.value = new Set()
        lastRegionUnitIds.value = new Set()
        lastSectorUnits.value = []
        lastRegionUnits.value = []
        lastManualUnits.value = []
        bulkExcludedUnitIds.value = new Set()
      }
    )
    watch(
      () => sectorIds.value,
      (newIds) => {
        fetchUnitsBySectorAndFillTable(newIds)
      },
      { deep: true }
    )
    watch(
      () => regionIds.value,
      (newIds) => {
        fetchUnitsByRegionAndFillTable(newIds)
      },
      { deep: true }
    )

    return {
      isCreateMode,
      areDependentFieldsDisabled,
      isDivisionRegionUnitsSelectionLocked,
      planningId,
      loading,
      loadError,
      currentItem,
      activityType,
      shouldFilterMainUnits,
      sectorIds,
      regionIds,
      selectedUnitIds,
      tableRows,
      planningTitle,
      selectedUnitsTitle,
      tableFields,
      allSelectedCreate,
      isIndeterminateCreate,
      toggleSelectAllCreate,
      allSelectedEdit,
      isIndeterminateEdit,
      toggleSelectAllEdit,
      hasSelectedRowsCreate,
      activityTypeOptions,
      unitsInitialOptionsForSelect,
      localSectorOptions,
      localRegionOptions,
      localUserOptions,
      userOptions,
      reviewerOptions,
      editableItems,
      editableItemsTableFields,
      editModeTableItems,
      distinctPropertyTypesInPlanningTable,
      selectedPropertyTypeLabelsCreate,
      selectedPropertyTypeLabelsEdit,
      propertyTypeFilterSelectOptions,
      reducePlanningPropertyTypeOption,
      onCreatePropertyTypesFilterInput,
      onEditPropertyTypesFilterInput,
      editableItemsTableFieldsResolved,
      itemsSaving,
      isReviewMode,
      isReviewFlowMode,
      showAddExtraUnits,
      selectedExtraUnitIds,
      reviewExtraItems,
      reviewExtraUnitsInitialOptions,
      showSendToReviewBlock,
      showApproveRejectBlock,
      showSecondApprovalAcceptBlock,
      showSendProgrammingBlock,
      showEditableItemsTable,
      shouldShowSavePlanningItemsButton,
      statusChangeCommentsWithText,
      isReleasedStatus,
      statusCode,
      isEditLocked,
      isPlanningHeaderOrTableShellDisabled,
      isDraftPlanningEditBlocked,
      isPlanningRowCheckboxDisabled,
      isPlanningRowCommentDisabled,
      isPlanningRowFieldsDisabled,
      getPlanningEditTableRowClass,
      editRowsForBulkSelectInEditMode,
      isTrashActionAllowed,
      canCreatePlanning,
      canUpdatePlanning,
      canDeletePlanning,
      selectedReviewerId,
      selectedSecondApproverId,
      approverOptions,
      showRejectModal,
      rejectComment,
      createSubmitting,
      workflowLoading,
      formatDate,
      goBack,
      reduceOptionToValue,
      withPopperTop,
      filterUsersByNameEmailAndUser,
      commentEndsWithDeletionMarker,
      planningCommentDisplayValue,
      onStartDateChange,
      onExtraUnitsInput,
      toggleAddExtraUnits,
      onAddExtraUnits,
      onUnitsInput,
      removeRow,
      removeEditTableRow,
      onTrashClick,
      onPlanningCommentFieldInput,
      onSubmitCreate,
      onUnifiedSavePlanningItems,
      onSendToReview,
      onSendToSecondApproval,
      onAcceptProgramming,
      onRejectConfirm,
      onSendProgramming,
      onDownloadPlanning,
    }
  },
}
</script>

<style lang="scss" scoped>
.planning-page-header-row {
  margin-left: 0;
  margin-right: 0;
}
.planning-header-left-col {
  min-width: 0;
}
.planning-page-header-actions {
  gap: 0.5rem;
}

.planning-form-card {
  ::v-deep .card-body {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .planning-fields-row {
    flex: 0 0 auto;
    padding-bottom: 0.5rem;
  }
  .selected-units-card {
    flex: 0 1 auto;
    display: flex;
    flex-direction: column;
    overflow: visible;
    .card-body,
    .selected-units-card-body {
      flex: 0 1 auto;
      overflow: visible;
    }
  }
  .planning-units-table-scroll {
    min-height: 0;
    max-height: min(52vh, 40rem);
    overflow-x: auto;
    overflow-y: auto;
  }
  .planning-units-table-scroll ::v-deep thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #f3f2f7;
    box-shadow: 0 1px 0 rgba(34, 41, 47, 0.1);
  }
  .planning-units-table {
    width: 100%;
    table-layout: auto;
    ::v-deep table {
      width: 100%;
      table-layout: auto;
    }
    ::v-deep tr.planning-row-inactive td {
      background-color: #f3f2f7 !important;
    }
    ::v-deep th,
    ::v-deep td {
      vertical-align: middle;
    }
    ::v-deep th:first-child,
    ::v-deep td:first-child,
    ::v-deep th:last-child,
    ::v-deep td:last-child {
      white-space: nowrap;
    }
    ::v-deep .assigned-to-cell {
      width: 32rem;
      min-width: 32rem;
      max-width: 32rem;
      box-sizing: border-box;
    }
  }
  .planning-form-actions-row {
    flex-shrink: 0;
    margin-left: -1.25rem;
    margin-right: -1.25rem;
    margin-bottom: -1.25rem;
  }
  .planning-form-actions-col {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem 1.25rem 1.25rem;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }
  .planning-form-actions-left {
    flex: 1 1 auto;
    min-width: 0;
  }
}
.planning-comment-cell--pending-delete ::v-deep textarea.form-control {
  border-bottom: 2px solid rgba(220, 53, 69, 0.4);
}

.planning-title-col {
  padding-left: 1.25rem;
}
.assigned-to-cell {
  width: 520px;
  min-width: 520px;
  max-width: 520px;
  box-sizing: border-box;
}
.review-extra-units-wrapper {
  min-width: 420px;
}
::v-deep .vs--disabled {
  opacity: 1;
}
::v-deep .vs--disabled .vs__selected,
::v-deep .vs--disabled .vs__search,
::v-deep .vs--disabled .vs__actions {
  color: #5e5873 !important;
  -webkit-text-fill-color: #5e5873;
  opacity: 1 !important;
}

.planning-status-change-history {
  padding-top: 0.75rem;
  margin-top: 0.25rem;
  border-top: 1px solid rgba(34, 41, 47, 0.08);
}

.planning-status-change-history__item {
  border-radius: 0.357rem;
  padding: 0.65rem 0.85rem;
  margin-bottom: 0.5rem;
  background-color: #f8f8fc;
  border: 1px solid #ebe9f1;
  color: #5e5873;
}

.planning-status-change-history__meta {
  color: #6e6b7b;
}

.planning-status-change-history__transition {
  color: #5e5873;
}

.planning-status-change-history__comment {
  color: #4b465c;
}

.dark-layout .planning-comment-cell--pending-delete ::v-deep textarea.form-control {
  border-bottom-color: rgba(255, 107, 122, 0.55);
}

.dark-layout .planning-status-change-history {
  border-top-color: rgba(255, 255, 255, 0.08);
}

.dark-layout .planning-status-change-history__item {
  background-color: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
  color: #d0d2d6;
}

.dark-layout .planning-status-change-history__meta {
  color: #a3a8b6;
}

.dark-layout .planning-status-change-history__transition {
  color: #d0d2d6;
}

.dark-layout .planning-status-change-history__comment {
  color: #e4e6eb;
}

.dark-layout .planning-status-change-history__user {
  color: #f6f6f9;
}

.dark-layout .planning-units-table-scroll ::v-deep thead th {
  background-color: #283046;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.08);
}

.dark-layout .planning-form-card .planning-units-table ::v-deep tr.planning-row-inactive td {
  background-color: rgba(255, 255, 255, 0.05) !important;
}
</style>
